// Enum for document types
export enum DocumentType {
  ADHAR = "ADHAR",
  // Add other document types as needed
}
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getMasterOptionName } from "./formDataHelper";
import { JOURNEY_EMAIL_REGEX } from "../../../utils/emailValidation";
import {
  Grid,
  Box,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  Skeleton,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState, store, persistor } from "../../../services/redux/store";
import AddIcon from "@mui/icons-material/Add";
import postsServices from "../../../services/api/service/postsService";
import ModalPopup from "../ModalPopup";
import { PrimaryButton } from "../Button";
import { IconButton } from "../Button";
import {
  removeAadhaarVerifiedDetailsCoApplicant,
  removeCoApplicantVerification,
  setAadhaarApplicationNoCoApplicant,
  setHorizontalSteps,
  updateFormData,
  removeIsDocumentVerifiedCoApplicant,
  removeAadhaarApplicationNoCoApplicant,
  setBreStatus,
  removeFormDataKeys,
  setActiveHorizontalIndex,
  setLoanDetailCoApplicantCollapseValue,
  setVerificationMessage,
  setDisableAddMoreCoApplicant,
  setSavedBasicForCoApplicant,
  removeSavedBasicForCoApplicant,
  setEncryptionKey,
} from "../../../services/redux/slices/stepperDynamicFormSlice";
import CollapsibleSection from "../../../pages/common-features/CollapsibleSection";
import { SectionHeader } from "../SectionHeader";
import idIcon from "./../../../assets/svgIcons/id.svg";
import locationIcon from "./../../../assets/svgIcons/live-view.svg";
import RightBlackArrow from "./../../../assets/svgIcons/right-black-arrow.svg";
import VerifiedIcon from "./../../../assets/svgIcons/check-circle.svg";
import RemoveIcon from "./../../../assets/svgIcons/remove-icon.svg";
import LoaderBackdrop from "../LoaderBackdrop";
import { setStatusPopup } from "../../../services/redux/slices/statusPopupSlice";
import applicantDetailsServices from "../../../services/api/service/applicantDetailsService";

import losServices, {
  SaveCoApplicantBody,
} from "../../../services/api/service/losService";
import dropdownService from "../../../services/api/service/dropdownService";
import {
  FirstCoApplicantEmploymentNotice,
  ensureFirstCoApplicantEmploymentYes,
  isFirstCoApplicantEmploymentField,
  lockFirstCoApplicantEmploymentField,
} from "./coApplicantEmploymentLock";
import { mapGender, handleOwnershipStatusValue, resolveBirthCityId } from "../../../utils/helpers";
import { hasCoApplicantPayPartyChanged, getFetchedCoApplicantPayParty } from "./helperDynamic";
import { buildCoApplicantSaveIdDto } from "./helperDto";
import { useApplicantMasterData } from "../../../hooks/useApplicantMasterData";
import { fetchUserApplicationsFormData } from "../../../services/api/service/userApplicationService";
import applicationDetailsServices from "../../../services/api/service/applicationDetails";
import { trackCustomerJourneyEvent, trackCustomerJourneyEventAndWait } from "../../../utils/ga";
import { decrypt, encryptData } from "../../../utils/crypto";
import { getSanitizedPublicKeyFromResponse } from "../../../utils/piiKey";
import {
  getGAProvisionalOfferStatements,
  targetAddressFields,
} from "../../../components/ui/StepperDyanmicForm/helper";


type EmploymentTypeOption = "Salaried" | "SEP" | "SENP" | "Student" | "Others";
type GenderOption = "male" | "female" | "other" | "prefer not to say";

const getAutocompleteOptionValue = (value: any): string => {
  if (value && typeof value === "object") {
    return String(value?.value ?? value?.label ?? "").trim();
  }

  return String(value ?? "").trim();
};

const resolveAutocompleteOption = (value: any, options: any[] = []) => {
  const optionValue = getAutocompleteOptionValue(value);

  if (!optionValue) {
    return null;
  }

  return (
    (options || []).find(
      (option: any) =>
        String(option?.value ?? "").trim().toLowerCase() ===
          optionValue.toLowerCase() ||
        String(option?.label ?? "").trim().toLowerCase() ===
          optionValue.toLowerCase(),
    ) || null
  );
};

// Monthly Obligation and Monthly Income must stay mandatory for every employment
// type (Farmer, Others, Salaried, SEP, SENP), regardless of what the field config says.
const isMandatoryEmploymentAmountField = (name: string): boolean =>
  /^employmentCoApplicant\d+monthlyObligation$/i.test(name) ||
  /^employmentCoApplicant\d+monthlyIncome$/i.test(name);

const withMandatoryEmploymentAmount = (field: any) =>
  isMandatoryEmploymentAmountField(String(field?.name || ""))
    ? { ...field, required: true }
    : field;

// Company name and company address (address lines, landmark, pincode) become
// mandatory once the co-applicant's employment type is Salaried/SEP/SENP, even
// though the field config marks these fields optional by default.
const MANDATORY_COMPANY_ADDRESS_EMPLOYMENT_TYPES = ["Salaried", "SEP", "SENP"];

const COMPANY_ADDRESS_FIELD_REGEX =
  /^employmentCoApplicant(\d+)(companyName|companyAddress1|companyAddress2|companyAddress3|landmark|pincode|city|state|country)$/i;

const withMandatoryCompanyAddress = (field: any, formVals: any) => {
  const match = String(field?.name || "").match(COMPANY_ADDRESS_FIELD_REGEX);
  if (!match) return field;

  const coAppNum = match[1];
  const employmentType =
    formVals?.[`employmentCoApplicant${coAppNum}employmentType`];
  return MANDATORY_COMPANY_ADDRESS_EMPLOYMENT_TYPES.includes(employmentType)
    ? { ...field, required: true }
    : field;
};

const getAutocompleteOptionOrValue = (value: any): string => {
  if (value && typeof value === "object") {
    return String(value?.value ?? value?.label ?? "").trim();
  }

  return String(value ?? "").trim();
};

const CoApplicantDetailsSection = ({
  fields,
  renderField,
  formikRef,
  values,
  errors,
  horizontalSteps,
  activeVerticalIndex,
  activeHorizontalIndex,
  pinCodeData,
  dropdownData,
  handleInvokZipCode,
  relationshipOptionsList,
  salutationOptionsList,
  zipCodeLoading,
  tabIndex,
  setTabIndex,
  checkIsLeadApiInvoked,
  handleLeadCreationFlow,
  getLeadPayload,
}: any) => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    templateId,
    applicationId,
    trackingId,
    isDocumentVerifiedCoApplicant,
    coApplicantZipCodeData,
    formData,
    coApplicantVerification,
    disableAddMoreCoApplicant,
    savedBasicCoApplicants,
    loanDetailCoApplicantCollapseValue,
    encryptionKey,
    fetchUserApplicationData,
  } = useSelector((state: RootState) => state.stepper);
  const reduxHorizontalSteps = useSelector(
    (state: RootState) => state?.stepper?.horizontalSteps
  );
  const applicationSource = useSelector((state: RootState) => state?.stepper?.applicationSource);
  const leadSequenceId = useSelector((state: RootState) => state?.stepper?.leadSequenceId);

  const { userProfile } = useSelector((state: RootState) => state.auth);
  const {
    fetchCitiesByCountryState,
    getCityOptionsForCountry,
  } = useApplicantMasterData();
  const prevValuesRef: any = useRef({});
  const prevSelectedCoApplicantIndexRef = useRef<number | null>(null);
  const prevSameAsValueRef = useRef<string | undefined>(undefined);
  const skipResetAfterSameAsRef = useRef(false);
  const hasDefaultedCollateralRef = useRef(false);

  const handleGAProvisionalOfferStatments = useMemo(
    () => getGAProvisionalOfferStatements(applicationSource),
    [applicationSource]
  );

  const fetchSanitizedPiiPublicKey = useCallback(async () => {
    if (encryptionKey) {
      return getSanitizedPublicKeyFromResponse(encryptionKey);
    }
    const piiKeyResponse: any = await losServices.getPiiKey();
    if (piiKeyResponse?.status !== 200) return "";

    const rawKey = piiKeyResponse?.data?.p_k;
    const rawKeyStr = typeof rawKey === "string" ? rawKey : "";
    dispatch(setEncryptionKey(rawKeyStr));

    return getSanitizedPublicKeyFromResponse(rawKey);
  }, [encryptionKey, dispatch]);

  // Function to validate Redux schema fields against fetched application data
  const validateReduxFieldsWithFetchedData = async () => {
    try {
      const token: any = process.env.REACT_APP_FETCH_TOKEN;
      const res: any = await fetchUserApplicationsFormData(token, trackingId);
      const apps = res.data?.applications || [];

      // Check for isEmployed field in Loan Details to disable Add More Co-Applicant
      const applicantLoanDetailsStep = apps?.[0]?.horizontalSteps?.find(
        (step: any) => step.horizontalStepName === "Applicant & Loan Details"
      );

      if (applicantLoanDetailsStep) {
        const loanDetailsVerticalStep =
          applicantLoanDetailsStep.verticalSteps?.find(
            (vStep: any) => vStep.verticalStepName === "Co-Applicant Details"
          );

        if (loanDetailsVerticalStep?.fields) {
          // Collect all employment-related fields from all field objects
          const employmentData: Record<string, any> = {};

          loanDetailsVerticalStep.fields.forEach((field: any) => {
            Object.keys(field).forEach((key) => {
              if (
                /^employmentCoApplicant\d+(isEmployed|employmentType)$/.test(
                  key
                )
              ) {
                employmentData[key] = field[key];
              }
            });
          });

          // Find all isEmployed fields that have values
          const isEmployedKeys = Object.keys(employmentData).filter(
            (key) =>
              /^employmentCoApplicant\d+isEmployed$/.test(key) &&
              employmentData[key] &&
              employmentData[key] !== ""
          );

          // Check if all isEmployed="Yes" have corresponding non-blank employmentType
          let hasValidEmployment = false;
          if (isEmployedKeys.length > 0) {
            hasValidEmployment = isEmployedKeys.every((isEmployedKey) => {
              const value = employmentData[isEmployedKey];

              if (value === "Yes") {
                // Extract co-applicant number
                const match = isEmployedKey.match(
                  /^employmentCoApplicant(\d+)isEmployed$/
                );
                if (match) {
                  const coApplicantNum = match[1];
                  const employmentTypeKey = `employmentCoApplicant${coApplicantNum}employmentType`;

                  // Check if employmentType exists and has a value
                  return (
                    employmentData[employmentTypeKey] &&
                    employmentData[employmentTypeKey] !== ""
                  );
                }
              }

              // If isEmployed is not "Yes", we don't need to check employmentType
              return true;
            });
          }

          if (hasValidEmployment) {
            dispatch(setDisableAddMoreCoApplicant(false));
          }
        }
      }

      // const lengthOfHorizontalSteps = apps?.[0]?.horizontalSteps?.length;
      // let apiFormData: any = {};
      // for (let i = 0; i < lengthOfHorizontalSteps; i++) {
      //   const verticalSteps =
      //     apps?.[0]?.horizontalSteps?.[i]?.verticalSteps;
      //   for (let j = 0; j < verticalSteps?.length; j++) {
      //     const fields = verticalSteps?.[j]?.fields;
      //     for (let k = 0; k < fields?.length; k++) {
      //       const field = fields?.[k];
      //       apiFormData = { ...apiFormData, ...field };
      //     }
      //   }
      // }

      // console.log(apiFormData, "apiFormData")
      // dispatch(updateFormData(apiFormData));


      // Additional check: Compare Redux schema fields with fetched application data
      if (reduxHorizontalSteps && Array.isArray(reduxHorizontalSteps)) {
        const reduxApplicantLoanStep = reduxHorizontalSteps.find(
          (step: any) => step.stepLabel === "Applicant & Loan Details"
        );

        if (reduxApplicantLoanStep) {
          const reduxLoanDetailsStep =
            reduxApplicantLoanStep.verticalSteps?.find(
              (vStep: any) => vStep.stepLabel === "Co-Applicant Details"
            );

          if (reduxLoanDetailsStep?.fields) {
            // Get all employmentCoApplicant{N}isEmployed field names from Redux schema
            const reduxIsEmployedFields = reduxLoanDetailsStep.fields
              .map((field: any) => field.name)
              .filter((name: string) =>
                /^employmentCoApplicant\d+isEmployed$/.test(name)
              );

            if (reduxIsEmployedFields.length > 0) {
              // Get all employmentCoApplicant{N}isEmployed fields that have values in fetched data
              const fetchedApplicantLoanStep = apps?.[0]?.horizontalSteps?.find(
                (step: any) =>
                  step.horizontalStepName === "Applicant & Loan Details"
              );

              if (fetchedApplicantLoanStep) {
                const fetchedLoanDetailsStep =
                  fetchedApplicantLoanStep.verticalSteps?.find(
                    (vStep: any) => vStep.verticalStepName === "Co-Applicant Details"
                  );

                if (fetchedLoanDetailsStep?.fields) {
                  // Collect all isEmployed fields that have values in fetched data
                  const fetchedIsEmployedFields = new Set<string>();

                  fetchedLoanDetailsStep.fields.forEach((field: any) => {
                    Object.keys(field).forEach((key) => {
                      if (
                        /^employmentCoApplicant\d+isEmployed$/.test(key) &&
                        field[key] &&
                        field[key] !== ""
                      ) {
                        fetchedIsEmployedFields.add(key);
                      }
                    });
                  });

                  // Check if any Redux schema fields are missing in fetched data
                  const missingFields = reduxIsEmployedFields.filter(
                    (fieldName: string) =>
                      !fetchedIsEmployedFields.has(fieldName)
                  );

                  // If Redux has fields that are not present in fetched data, disable add more
                  if (missingFields.length > 0) {
                    dispatch(setDisableAddMoreCoApplicant(true));
                  }
                }
              }
            }
          }
        }
      }
    } catch (err) {
      console.error("Error validating Redux fields with fetched data:", err);
    }
  };

  // Validate Redux fields on component mount
  useEffect(() => {
    if (applicationId && trackingId) {
      validateReduxFieldsWithFetchedData();
    }
  }, []);

  // Default value to 'No' for collateral. This must be set synchronously
  // during render (not in a useEffect) so the dropdown's very first paint
  // already shows "No" selected instead of blank-then-flipping-to-"No" a
  // tick later. hasDefaultedCollateralRef guards it to run this check only
  // once — re-running it on every render (the old inline-IIFE approach) kept
  // forcing "collateral" back to "No" on later re-renders too (e.g. the one
  // triggered by selecting a Collateral Type option), hiding the just-shown
  // Collateral Type/Amount fields.
  useEffect(() => {
  const currentCollateral = formikRef.current?.values?.["collateral"];

  // Formik values load hote hi check karein
  if (
    !hasDefaultedCollateralRef.current &&
    formikRef?.current &&
    (currentCollateral === undefined || currentCollateral === "")
  ) {
    hasDefaultedCollateralRef.current = true;
    
    // setTimeout microtask queue ensure karta hai ki Formik render pass finish ho chuka hai
    setTimeout(() => {
      formikRef.current?.setFieldValue("collateral", "No", false);
    }, 0);
  }
}, [formikRef.current?.values?.["collateral"]]);

  // Confirmation modal state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isSaveNextLoading, setIsSaveNextLoading] = useState(false);
  const [pendingRemoveIds, setPendingRemoveIds] = useState<number[] | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("sm"));
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity?: "error" | "success";
  }>({
    open: false,
    message: "",
    severity: "error",
  });
  // Aadhaar verification state per co-applicant
  const [isAadharLoading, setIsAadharLoading] = useState<
    Record<string, boolean>
  >({});
  const [coApplicantMaritalStatusOptions, setCoApplicantMaritalStatusOptions] =
    useState<Array<{ value: string; label: string }>>([]);
  const [coApplicantNationalityOptions, setCoApplicantNationalityOptions] =
    useState<Array<{ value: string; label: string }>>([]);
  const coAppNums = fields
    .map((f: any) => {
      const match = f.name.match(/^(basic|address|employment)CoApplicant(\d+)/);
      return match ? parseInt(match[2], 10) : null;
    })
    .filter((n: number | null) => n !== null);
  const numberOfCoApp = Object.keys(
    coAppNums.reduce((acc: any, curr: any) => {
      acc[curr] = true;
      return acc;
    }, {} as Record<number, boolean>)
  ).length;
  // Collapsible state
  const [expanded, setExpanded] = React.useState<string | false>(
    loanDetailCoApplicantCollapseValue ||
    (typeof window !== "undefined"
      ? localStorage.getItem("coAppExpandedKey")
      : null) ||
    `CoApplicant${numberOfCoApp}`
  );
  const [selectedCoApplicantIndex, setSelectedCoApplicantIndex] = useState<number | null>(null);

  // Auto-hydrate permanent/current/company zip data for the expanded co-applicant from
  // whatever pincode values are already present (typed or prefilled on resume), instead of
  // relying solely on the Address tab being clicked / the pincode field's onChange firing.
  const lastZipLookupRef = useRef<Record<string, string>>({});
  const invokeZipCodeLookup = useCallback(
    (zipCode: string, coAppNum: string, addressType: "permanent" | "current" | "company") => {
      const sanitizedZip = String(zipCode || "").replace(/\D/g, "");
      const lookupKey = `${coAppNum}_${addressType}`;

      if (sanitizedZip.length !== 6) {
        delete lastZipLookupRef.current[lookupKey];
        return;
      }

      if (lastZipLookupRef.current[lookupKey] === sanitizedZip) return;
      lastZipLookupRef.current[lookupKey] = sanitizedZip;
      handleInvokZipCode(sanitizedZip, coAppNum, addressType);
    },
    [handleInvokZipCode]
  );

  useEffect(() => {
    if (typeof expanded !== "string") return;
    const coAppNum = expanded.match(/^CoApplicant(\d+)$/)?.[1];
    if (!coAppNum) return;

    // Read live Formik values first, falling back to formData — "Keep the
    // address same as" copies the permanent pincode into the current pincode
    // via formikRef.setFieldValue only (it never dispatches to Redux), so
    // relying on formData alone misses that copy and the current-address
    // zipcode lookup never fires (breaking create-update-demographic).
    const liveValues = formikRef?.current?.values;

    invokeZipCodeLookup(
      liveValues?.[`addressCoApplicant${coAppNum}permanentPincode`] ??
        formData?.[`addressCoApplicant${coAppNum}permanentPincode`],
      coAppNum,
      "permanent"
    );
    invokeZipCodeLookup(
      liveValues?.[`addressCoApplicant${coAppNum}currentPincode`] ??
        formData?.[`addressCoApplicant${coAppNum}currentPincode`],
      coAppNum,
      "current"
    );
    invokeZipCodeLookup(
      liveValues?.[`employmentCoApplicant${coAppNum}pincode`] ??
        formData?.[`employmentCoApplicant${coAppNum}pincode`],
      coAppNum,
      "company"
    );
  }, [expanded, formData, formikRef?.current?.values, invokeZipCodeLookup]);

  // Calculate next co-applicant number based on field names (support all prefixes)
  // Master options sourcing for dynamic co-applicant fields
  const relationshipOptions =
    fields?.find((f: any) => f?.name?.includes("RelationWithStudent"))?.options
      ?.options ?? [];
  const payPartyOptions =
    fields?.find((f: any) => f?.name?.includes("PayParty"))?.options?.options ??
    [];
  const permanentOwnershipStatusOptions =
    fields?.find((f: any) => f?.name?.includes("permanentOwnershipStatus"))
      ?.options?.options ?? [];
  const currentOwnershipStatusOptions =
    fields?.find((f: any) => f?.name?.includes("currentOwnershipStatus"))
      ?.options?.options ?? [];
  const isEmployedOptions =
    fields?.find((f: any) => f?.name?.includes("isEmployed"))?.options
      ?.options ?? [];
  const employmentTypeOptions: EmploymentTypeOption[] =
    fields?.find((f: any) => f?.name?.includes("employmentType"))?.options
      ?.options ?? [];
  const genderOptions: GenderOption[] =
    fields?.find((f: any) => f?.name?.includes("Gender"))?.options?.options ??
    [];
  const maritalStatusOptions =
    coApplicantMaritalStatusOptions.length > 0
      ? coApplicantMaritalStatusOptions
      : dropdownData?.maritalStatuses ?? [];
  const nationalityOptions =
    coApplicantNationalityOptions.length > 0
      ? coApplicantNationalityOptions
      : [];
  const countryOptions = dropdownData?.countries ?? [];

  useEffect(() => {
    let isMounted = true;
    const fetchMaritalStatuses = async () => {
      const fromDropdown = dropdownData?.maritalStatuses ?? [];
      if (Array.isArray(fromDropdown) && fromDropdown.length > 0) {
        if (isMounted) setCoApplicantMaritalStatusOptions(fromDropdown);
        return;
      }
      try {
        const response = await dropdownService.getMaritalStatusMaster();
        const fetched = (response?.data || [])
          .map((item: any) => ({
            value: String(item?.maritalStatusCode ?? item?.code ?? item?.value ?? ""),
            label: String(item?.maritalStatusName ?? item?.maritalStatusDesc ?? item?.name ?? item?.desc ?? item?.label ?? ""),
          }))
          .filter((opt: any) => opt.value && opt.label && opt.value !== "undefined" && opt.label !== "undefined");
        if (isMounted) setCoApplicantMaritalStatusOptions(fetched);
      } catch (error) {
        console.error("Error fetching marital statuses:", error);
      }
    };
    fetchMaritalStatuses();
    return () => { isMounted = false; };
  }, [dropdownData?.maritalStatuses]);

  useEffect(() => {
    let isMounted = true;
    const fetchNationalities = async () => {
      try {
        const fetched = await dropdownService.getNationalityList();
        if (isMounted && fetched.length > 0) {
          setCoApplicantNationalityOptions(fetched);
          // Set default nationality to 'Indian' for each co-applicant if not already set
          fields.forEach((field: any) => {
            if (/^basicCoApplicant\d+Nationality$/.test(field.name) || /naionality/.test(field.name.toLowerCase())) {
              const fieldName = field.name;
              const currentValue = formikRef?.current?.values?.[fieldName];
              if (!currentValue || (typeof currentValue === "object" && !currentValue.value && !currentValue.label)) {
                const indiaOption = fetched.find(opt => opt.label?.toLowerCase() === "indian" || opt.value?.toLowerCase() === "indian" || opt.value?.toLowerCase() === "in");
                if (indiaOption) {
                  formikRef?.current?.setFieldValue(fieldName, indiaOption.label, false);
                }
              }
            }
          });
        }
      } catch (error) {
        console.error("Error fetching nationalities:", error);
      }
    };
    fetchNationalities();
    return () => { isMounted = false; };
  }, [fields, formikRef]);
  const getRelationValueFieldMatch = (fieldName: string) =>
    fieldName.match(
      /^basicCoApplicant(\d+)(OtherRelationWithStudent|RelationValue)$/,
    );
  const getRelationValueFieldCandidates = (coAppNum: string | number) => [
    `basicCoApplicant${coAppNum}OtherRelationWithStudent`,
    `basicCoApplicant${coAppNum}RelationValue`,
  ];
  const getRelationValueFieldName = (coAppNum: string | number) =>
    getRelationValueFieldCandidates(coAppNum).find((fieldName) =>
      fields?.some((field: any) => field?.name === fieldName),
    ) || `basicCoApplicant${coAppNum}OtherRelationWithStudent`;
  const hasVisibleRelationValueField = (coAppNum: string | number) =>
    formikRef?.current?.values?.[`basicCoApplicant${coAppNum}RelationWithStudent`] ===
    "OTHERS";
  const isRelationValueFieldRequired = (field: any) => {
    const match = getRelationValueFieldMatch(field?.name || "");
    if (!match) {
      return Boolean(field?.required);
    }
    return hasVisibleRelationValueField(match[1]);
  };

  const updateAddMoreStateFromCoApplicantResp = (resp: any) => {
    const formData = resp?.data?.data?.formData || [];

    const isEmployedFields = formData.filter(
      (item: any) =>
        item.fieldKey?.includes("employmentCoApplicant") &&
        item.fieldKey?.endsWith("isEmployed")
    );

    const hasAnyBlank = isEmployedFields.some(
      (item: any) =>
        item.fieldValue === "" ||
        item.fieldValue === null ||
        item.fieldValue === undefined
    );

    dispatch(setDisableAddMoreCoApplicant(hasAnyBlank));
  };

  // Get the current vertical step object
  const currentVerticalStep =
    horizontalSteps?.[activeHorizontalIndex]?.verticalSteps?.[
    activeVerticalIndex
    ];

  // Get the vertical step id
  const verticalStepId = currentVerticalStep?.id;
  const addCoApplicantFields = async () => {
    setIsLoading(true);
    try {
      // Fetch current co-applicant count from API and use count+1 as the next number
      let nextCoApplicantNum = 0;
      try {
        const countResp = await postsServices.addPost(
          "api/v1/applicant/get-coapplicant-count",
          { trackingId }
        );
        const apiCount = countResp?.data?.data?.count;
        if (typeof apiCount !== "number") {
          dispatch(
            setStatusPopup({
              status: "error",
              message: "Unable to fetch co-applicant count. Please try again.",
            })
          );
          return;
        }
        // nextCoApplicantNum = apiCount === 0 ? 2 : apiCount + 1;
        nextCoApplicantNum = apiCount + 1;
        setExpanded(`CoApplicant${nextCoApplicantNum}`);
        setSelectedCoApplicantIndex(nextCoApplicantNum);
        localStorage.setItem("coAppExpandedKey", `CoApplicant${nextCoApplicantNum}`);
        dispatch(setDisableAddMoreCoApplicant(true));
      } catch (e) {
        dispatch(
          setStatusPopup({
            status: "error",
            message: "Unable to fetch co-applicant count. Please try again.",
          })
        );
        return;
      }
      const saveAsOptions = ["Applicant"]
      for(let i=0; i< nextCoApplicantNum; i++) {
        saveAsOptions.push(`Co-Applicant ${i + 1}`);
      }

      const apiFields = [
        // Basic Details
        {
          label: "Relationship with Student",
          name: `basicCoApplicant${nextCoApplicantNum}RelationWithStudent`,
          type: "autocomplete",
          required: true,
          options: { options:relationshipOptions },
        },
        {
          label: "Specify Relationship",
          name: `basicCoApplicant${nextCoApplicantNum}OtherRelationWithStudent`,
          required: false,
          type: "text",
          options: null,
        },
        {
          label: "Specify Relationship",
          name: `basicCoApplicant${nextCoApplicantNum}OtherRelationWithStudent`,
          required: false,
          type: "text",
          options: null,
        },
        {
          label: "How should we address you? (Mr./Ms./etc.)",
          name: `basicCoApplicant${nextCoApplicantNum}salutation`,
          type: "dropdown",
          required: true,
          options: {
            options: salutationOptionsList,
          },
        },
        {
          label: "Co-Applicant First Name",
          name: `basicCoApplicant${nextCoApplicantNum}FirstName`,
          type: "text",
          required: true,
        },
        {
          label: "Co-Applicant Last Name",
          name: `basicCoApplicant${nextCoApplicantNum}LastName`,
          type: "text",
          required: true,
        },
        {
          label: "Gender",
          name: `basicCoApplicant${nextCoApplicantNum}Gender`,
          type: "dropdown",
          options: { options: genderOptions },
          required: true,
        },
        {
          label: "Date of Birth",
          name: `basicCoApplicant${nextCoApplicantNum}dob`,
          type: "date",
          required: true,
        },
        {
          label: "PAN Number",
          name: `basicCoApplicant${nextCoApplicantNum}PanNumber`,
          type: "text",
          required: true,
        },
        {
          label: "Co-applicant mobile number",
          name: `basicCoApplicant${nextCoApplicantNum}PhoneNumber`,
          type: "phone",
          required: true,
        },
        {
          label: "Co-applicant email id",
          name: `basicCoApplicant${nextCoApplicantNum}Email`,
          type: "email",
          required: true,
        },
        {
          label: "PAN Verified",
          name: `basicCoApplicant${nextCoApplicantNum}IsPanVerified`,
          type: "boolean",
          required: false,
        },
        {
          label: "Mobile Verified",
          name: `basicCoApplicant${nextCoApplicantNum}IsMobileVerified`,
          type: "boolean",
          required: false,
        },
        {
          label: "Email Verified",
          name: `basicCoApplicant${nextCoApplicantNum}IsEmailVerified`,
          type: "boolean",
          required: false,
        },
        {
          label: "Nationality",
          name: `basicCoApplicant${nextCoApplicantNum}Nationality`,
          type: "autocomplete",
          required: true,
          options:nationalityOptions,
        },
        {
          label: "Marital Status",
          name: `basicCoApplicant${nextCoApplicantNum}MaritalStatus`,
          type: "dropdown",
          required: true,
          options: { options: maritalStatusOptions },
        },
        {
          label: "Country of Birth",
          name: `basicCoApplicant${nextCoApplicantNum}CountryOfBirth`,
          type: "autocomplete",
          required: true,
        },
        {
          label: "City of Birth",
          name: `basicCoApplicant${nextCoApplicantNum}CityOfBirth`,
          type: "autocomplete",
          required: true,
        },
        {
          label: "Aadhaar Verified",
          name: `basicCoApplicant${nextCoApplicantNum}AadhaarVerified`,
          type: "boolean",
          required: false,
        },
        {
          label:
            "Do you want to consider income of the co-applicant for loan evaluation?",
          name: `basicCoApplicant${nextCoApplicantNum}PayParty`,
          type: "radio",
          required: true,
          options: { options: payPartyOptions },
        },

        // Address Details (Permanent and Current)
        
        {
          label: "Keep the address same as",
          name: `addressCoApplicant${nextCoApplicantNum}SameAs`,
          type: "dropdown",
          required: false,
          options: { options: saveAsOptions },
        },
        {
          label: "Owner Ship Status",
          name: `addressCoApplicant${nextCoApplicantNum}permanentOwnershipStatus`,
          type: "dropdown",
          required: true,
          options: { options: permanentOwnershipStatusOptions },
        },
        {
          label: "How long have you been living here?",
          name: `addressCoApplicant${nextCoApplicantNum}permanentYearsInCurrentAddress`,
          type: "number",
          required: true,
        },
        {
          label: "How long have you lived in this city ",
          name: `addressCoApplicant${nextCoApplicantNum}permanentYearsInCurrentCity`,
          type: "number",
          required: true,
        },
        {
          label: "Address Line 1",
          name: `addressCoApplicant${nextCoApplicantNum}permanentAddressLine1`,
          type: "text",
          required: true,
        },
        {
          label: "Address Line 2",
          name: `addressCoApplicant${nextCoApplicantNum}permanentAddressLine2`,
          type: "text",
          required: false,
        },
        {
          label: "Address Line 3",
          name: `addressCoApplicant${nextCoApplicantNum}permanentAddressLine3`,
          type: "text",
          required: false,
        },
        {
          label: "Pincode",
          name: `addressCoApplicant${nextCoApplicantNum}permanentPincode`,
          type: "text",
          required: true,
        },
        {
          label: "City",
          name: `addressCoApplicant${nextCoApplicantNum}permanentCity`,
          type: "autocomplete",
          required: true,
        },
        {
          label: "State",
          name: `addressCoApplicant${nextCoApplicantNum}permanentState`,
          type: "autocomplete",
          required: true,
        },
        {
          label: "Country",
          name: `addressCoApplicant${nextCoApplicantNum}permanentCountry`,
          type: "autocomplete",
          required: true,
        },
        {
          label: "Keep Current Address same as Permanent Address",
          name: `addressCoApplicant${nextCoApplicantNum}currentsameAsPermanent`,
          type: "checkbox",
          required: false,
        },
        {
          label: "Owner Ship Status",
          name: `addressCoApplicant${nextCoApplicantNum}currentOwnershipStatus`,
          type: "dropdown",
          required: true,
          options: { options: currentOwnershipStatusOptions },
        },
        {
          label: "How long have you been living here?",
          name: `addressCoApplicant${nextCoApplicantNum}currentYearsInCurrentAddress`,
          type: "text",
          required: true,
        },
        {
          label: "How long have you lived in this city",
          name: `addressCoApplicant${nextCoApplicantNum}currentYearsInCurrentCity`,
          type: "text",
          required: true,
        },
        {
          label: "Address Line 1",
          name: `addressCoApplicant${nextCoApplicantNum}currentAddressLine1`,
          type: "text",
          required: true,
        },
        {
          label: "Address Line 2",
          name: `addressCoApplicant${nextCoApplicantNum}currentAddressLine2`,
          type: "text",
          required: false,
        },
        {
          label: "Address Line 3",
          name: `addressCoApplicant${nextCoApplicantNum}currentAddressLine3`,
          type: "text",
          required: false,
        },
        {
          label: "Pincode",
          name: `addressCoApplicant${nextCoApplicantNum}currentPincode`,
          type: "text",
          required: true,
        },
        {
          label: "City",
          name: `addressCoApplicant${nextCoApplicantNum}currentCity`,
          type: "autocomplete",
          required: true,
        },
        {
          label: "State",
          name: `addressCoApplicant${nextCoApplicantNum}currentState`,
          type: "autocomplete",
          required: true,
        },
        {
          label: "Country",
          name: `addressCoApplicant${nextCoApplicantNum}currentCountry`,
          type: "autocomplete",
          required: true,
        },

        // Employment Details
        {
          label: "Are you employed?",
          name: `employmentCoApplicant${nextCoApplicantNum}isEmployed`,
          type: "radio",
          required: true,
          options: { options: isEmployedOptions },
        },
        {
          label: "Employment Type",
          name: `employmentCoApplicant${nextCoApplicantNum}employmentType`,
          type: "dropdown",
          required: true,
          options: { options: employmentTypeOptions },
        },
        {
          label: "Monthly Obligation",
          name: `employmentCoApplicant${nextCoApplicantNum}monthlyObligation`,
          type: "number",
          required: true,
        },
        {
          label: "Company Name",
          name: `employmentCoApplicant${nextCoApplicantNum}companyName`,
          type: "text",
          required: true,
        },
        {
          label: "Designation",
          name: `employmentCoApplicant${nextCoApplicantNum}designation`,
          type: "autocomplete",
          required: true,
        },
        {
          label: "Monthly Income",
          name: `employmentCoApplicant${nextCoApplicantNum}monthlyIncome`,
          type: "number",
          required: true,
        },
        {
          label: "Year",
          name:  `employmentCoApplicant${nextCoApplicantNum}employmentYear`,
          required: true,
          type: "number"
        },
        {
          label: "Month",
          name:`employmentCoApplicant${nextCoApplicantNum}employmentMonth`,
          type: "number",
          required: true,
        },
        // ── Company Address — FIX 8: using companyField helper ──
        {
          label: "Address Line 1",
          name: `employmentCoApplicant${nextCoApplicantNum}companyAddress1`,
          type: "text",
          required: false,
        },
        {
          label: "Address Line 2",
          name: `employmentCoApplicant${nextCoApplicantNum}companyAddress2`,
          type: "text",
          required: false,
        },
        {
          label: "Address Line 3",
          name: `employmentCoApplicant${nextCoApplicantNum}companyAddress3`,
          type: "text",
          required: false,
        },
        {
          label: "Landmark",
          name: `employmentCoApplicant${nextCoApplicantNum}landmark`,
          type: "text",
          required: false,
        },
        {
          label: "Pincode",
          name: `employmentCoApplicant${nextCoApplicantNum}pincode`,
          type: "number",
          required: false,
        },
        {
          label: "City",
          name:`employmentCoApplicant${nextCoApplicantNum}city`,
          type: "text",
          required: false,
        },
        {
          label: "State",
          name: `employmentCoApplicant${nextCoApplicantNum}state`,
          type: "text",
          required: false,
        },
        {
          label: "Country",
          name: `employmentCoApplicant${nextCoApplicantNum}companyCountry`,
          type: "autocomplete",
          required: false,
        },

      ];
      const resp = await postsServices.addPost(
        "api/v1/product/store-user-fields",
        {
          vertical_step_id: verticalStepId,
          trackingId: trackingId,
          fields: apiFields,
        }
      );
      if (resp?.data?.status === "success" && Array.isArray(resp?.data?.data)) {
        const newFields = resp.data.data.map((f: any) => ({
          ...f,
          options: f?.options ? f?.options : null,
        }));

        // --- PUSH new fields into the correct vertical step (id: 9, Loan Details) in applicant_loan horizontal step in Redux state ---
        const state = store.getState();
        if (
          state &&
          state.stepper &&
          Array.isArray(state.stepper.horizontalSteps)
        ) {
          const horizontalSteps = [...state.stepper.horizontalSteps];

          const applicantLoanStepIdx = horizontalSteps.findIndex(
            (step: any) => step.stepKey === "applicant_loan"
          );

          if (applicantLoanStepIdx !== -1) {
            const applicantLoanStep = {
              ...horizontalSteps[applicantLoanStepIdx],
            };
            const verticalSteps = [...applicantLoanStep.verticalSteps];
            // Instead of hardcoding id === 9, use the current vertical step (activeVerticalIndex)
            const targetVerticalIdx = activeVerticalIndex;
            if (
              typeof targetVerticalIdx === "number" &&
              targetVerticalIdx >= 0 &&
              targetVerticalIdx < verticalSteps.length
            ) {
              const targetStep = { ...verticalSteps[targetVerticalIdx] };
              targetStep.fields = [...targetStep.fields, ...newFields];
              verticalSteps[targetVerticalIdx] = targetStep;
              applicantLoanStep.verticalSteps = verticalSteps;
              horizontalSteps[applicantLoanStepIdx] = applicantLoanStep;
              // Dispatch redux update using setHorizontalSteps action creator
              dispatch(setHorizontalSteps(horizontalSteps));
            }
          }
        }

        // Always overwrite Formik values for the new co-applicant fields to avoid stale data
        if (formikRef?.current) {
          newFields.forEach((field: any) => {
            let defaultValue: any = "";

            if (
              field.type === "dropdown" &&
              field.options?.options?.length > 0
            ) {
              // defaultValue =
              //   field.options.options[0]?.value ??
              //   field.options.options[0] ??
              //   "";
              defaultValue = ""; // Always empty by default
            } else if (field.type === "boolean") {
              defaultValue = false;
            }
            // Always overwrite, even if Formik already has a value for this key
            formikRef.current.setFieldValue(field.name, defaultValue, false);
          });
        }
      }
    } catch (err) {
      console.error("Add entry failed", err);
    } finally {
      setIsLoading(false);
    }
  };

  const removeCoApplicantFields = async (fieldIds: number[]) => {
    // Extract co-applicant index and number from field names (e.g., "coApplicant2Name" -> "1" for index '1')
    let coAppIndex: string | null = null;
    let coAppNum: string | null = null;
    const allFields = Array.isArray(fields)
      ? fields[0]?.fields
        ? fields.flatMap((g: any) => g.fields)
        : fields
      : [];

    for (const field of allFields) {
      if (fieldIds.includes(field.id)) {
        const match = field.name.match(
          /^(basic|address|employment)CoApplicant(\d+)/
        );

        if (match) {
          coAppIndex = String(Number(match[2]) - 1);
          coAppNum = match[2];
          break;
        }
      }
    }

    // If basic details for this co-applicant were previously saved, call delete-coapplicant API
    if (coAppNum) {
      const coAppKey = `CoApplicant${coAppNum}`;

      if (savedBasicCoApplicants?.[coAppKey]) {
        let isCoApplicantDeleted = true;
        try {
          // Fetch applicantId from LOS GetCoApplicantDetails; default to 0 if null/undefined
          const losPayload = {
            trackingId: trackingId,
            coApplicantType: `Co-Applicant ${coAppNum}`,
          };

          let applicantIdForDelete = "0";

          try {
            const resp: any =
              await losServices.getCoApplicantDetails(losPayload);
            const apiApplicantId = resp?.data?.data?.applicantId;
            applicantIdForDelete =
              apiApplicantId === null || apiApplicantId === undefined
                ? "0"
                : apiApplicantId || "0";
          } catch (e) {
            // If LOS call fails, proceed with 0
            applicantIdForDelete = "0";
          }

          const deleteResp: any = await applicationDetailsServices.deleteCoApplicant(
            trackingId,
            `Co-Applicant ${coAppNum}`,
          );
          const deleteStatus = deleteResp?.data?.status;
          if (deleteStatus && String(deleteStatus).toLowerCase() !== "success") {
            isCoApplicantDeleted = false;
          }
          // cleanup redux flag after successful delete
          if (isCoApplicantDeleted && savedBasicCoApplicants?.[coAppKey]) {
            dispatch(removeSavedBasicForCoApplicant(coAppKey));
          }
        } catch (err) {
          isCoApplicantDeleted = false;
          console.error("Error calling delete-coapplicant API", err);
        }
        // Saved co-applicant: delete-user-fields only runs once
        // delete-coapplicant succeeds.
        if (!isCoApplicantDeleted) {
          dispatch(
            setStatusPopup({
              status: "error",
              message: "Co-applicant cannot be deleted as they are added as a co-owner in collateral.",
            }),
          );
          return;
        }
      }
    }

    // Unsaved co-applicant: delete-user-fields is called straight away;
    // saved co-applicant: right after delete-coapplicant succeeded above.

    try {
      const resp = await postsServices.addPost(
        "api/v1/product/delete-user-fields",
        {
          form_field_ids: fieldIds,
          trackingId: trackingId,
        }
      );

      if (resp.data?.status === "success") {
        // Remove from Redux: Remove fields from horizontalSteps (vertical step id: 9 in applicant_loan)
        const state = store.getState();
        if (
          state &&
          state.stepper &&
          Array.isArray(state.stepper.horizontalSteps)
        ) {
          const horizontalSteps = [...state.stepper.horizontalSteps];
          const applicantLoanStepIdx = horizontalSteps.findIndex(
            (step: any) => step.stepKey === "applicant_loan"
          );
          if (applicantLoanStepIdx !== -1) {
            const applicantLoanStep = {
              ...horizontalSteps[applicantLoanStepIdx],
            };
            const verticalSteps = [...applicantLoanStep.verticalSteps];
            // Use the current vertical step (activeVerticalIndex) instead of hardcoded id === 9
            const targetVerticalIdx = activeVerticalIndex;
            if (
              typeof targetVerticalIdx === "number" &&
              targetVerticalIdx >= 0 &&
              targetVerticalIdx < verticalSteps.length
            ) {
              const targetStep = { ...verticalSteps[targetVerticalIdx] };
              targetStep.fields = targetStep.fields.filter(
                (field: any) => !fieldIds.includes(field.id)
              );
              verticalSteps[targetVerticalIdx] = targetStep;
              applicantLoanStep.verticalSteps = verticalSteps;
              horizontalSteps[applicantLoanStepIdx] = applicantLoanStep;
              // Dispatch redux update
              dispatch(setHorizontalSteps(horizontalSteps));
            }
          }
        }

        // Find field names to clear BEFORE removing from Redux
        const fieldNamesToClear: string[] = [];
        fields && fields?.forEach((group: any) => {
          group?.fields?.forEach((field: any) => {
            if (fieldIds.includes(field.id)) {
              fieldNamesToClear.push(field.name);
            }
          });
        });

        // Remove all keys for this co-applicant from Formik values ONLY, do not setFieldValue for those fields after
        if (formikRef?.current && coAppIndex !== null) {
          const prefix = `coApplicant${Number(coAppIndex) + 1}`;
          const newValues = { ...formikRef.current.values };
          Object.keys(newValues).forEach((key) => {
            if (key.startsWith(prefix)) {
              delete newValues[key];
            }
          });
          formikRef.current.setValues(newValues, false);
          // Force Formik to re-validate and re-render
          formikRef.current.validateForm();
        } else if (formikRef?.current) {
          // If not a co-applicant, just clear the fields as before
          fieldNamesToClear.forEach((name) => {
            const isBoolean =
              name?.toLowerCase().includes("is") ||
              name?.toLowerCase().includes("verified");
            formikRef.current.setFieldValue(
              name,
              isBoolean ? false : "",
              false
            );
          });
        }
        dispatch(setDisableAddMoreCoApplicant(hasRemainingUnsavedBlankCoApplicant(coAppNum)));
      }
    } catch (err) {
      console.error("Error deleting coapplicant fields", err);
    }

    // Remove verification state for this co-applicant
    if (coAppIndex !== null) {
      dispatch(removeCoApplicantVerification(coAppIndex));
    }
    // Remove Aadhaar verified details for this co-applicant from Redux
    if (coAppNum) {
      const coAppKey = `CoApplicant${coAppNum}`;
      dispatch(removeAadhaarVerifiedDetailsCoApplicant(coAppKey));
      dispatch(removeIsDocumentVerifiedCoApplicant(coAppKey));
      dispatch(removeAadhaarApplicationNoCoApplicant({ coAppNum, coAppKey }));

      // Remove all formData keys for this co-applicant from Redux and Formik
      if (formikRef?.current) {
        const values = { ...formikRef?.current?.values };
        const keysToRemove: string[] = [];
        // Remove all keys for this co-applicant (including empty/false values)
        const coAppRegex = new RegExp(
          `^(basic|address|employment)CoApplicant${coAppNum}`
        );
        Object.keys(values).forEach((key) => {
          if (coAppRegex.test(key)) {
            keysToRemove.push(key);
            delete values[key];
          }
        });
        if (keysToRemove.length > 0) {
          dispatch(removeFormDataKeys(keysToRemove));
        }
        // Also remove from Formik values
        formikRef?.current.setValues(values, false);
        formikRef?.current.validateForm();
      }
    }
  };

  // Handler for remove button click
  const handleRemoveClick = (ids: number[]) => {
    setPendingRemoveIds(ids);
    setConfirmOpen(true);
  };

  // Handler for confirm in modal
  const handleConfirmRemove = () => {
    if (pendingRemoveIds !== null) {
      removeCoApplicantFields(pendingRemoveIds);
    }
    setConfirmOpen(false);
    setPendingRemoveIds(null);
  };

  // Handler for cancel in modal
  const handleCancelRemove = () => {
    setConfirmOpen(false);
    setPendingRemoveIds(null);
  };

  const handleProceedClick = async () => {
    {
      const proceedValues = formikRef?.current?.values || {};
      const coAppNumsForPan = Array.from(
        new Set(
          fields
            .map(
              (f: any) =>
                String(f?.name || "").match(/^basicCoApplicant(\d+)/)?.[1]
            )
            .filter(Boolean) as string[]
        )
      );

      const unverifiedCoAppNum = coAppNumsForPan.find((num) => {
        const panValue = proceedValues[`basicCoApplicant${num}PanNumber`];
        // A blank PAN is already reported by its own required rule.
        if (!panValue || String(panValue).trim() === "") return false;

        const formikFlag = proceedValues[`basicCoApplicant${num}IsPanVerified`];
        // Redux is consulted alongside Formik — an effect keeps the two in sync,
        // so checking both avoids a false block from sync lag.
        const reduxFlag =
          coApplicantVerification?.[String(Number(num) - 1)]?.panVerified;

        return !(
          formikFlag === true ||
          formikFlag === "true" ||
          Boolean(reduxFlag)
        );
      });

      if (unverifiedCoAppNum) {
        const panFieldName = `basicCoApplicant${unverifiedCoAppNum}PanNumber`;
        formikRef?.current?.setFieldError(
          panFieldName,
          "PAN verification is pending. Please verify the PAN to continue."
        );
        formikRef?.current?.setFieldTouched(panFieldName, true, false);
        setSnackbar({
          open: true,
          message: `Co-Applicant ${unverifiedCoAppNum}'s PAN is not verified. Please verify it before proceeding.`,
          severity: "error",
        });
        return;
      }
    }

    dispatch(setBreStatus("FROM_FORM"));

    // Only send LoanDetailsLoanAmount and collateral fields, and if collateral is 'Yes', also collateralType and collateralAmount
    const data: any[] = [];
    const values = formikRef?.current?.values || {};
    const collateralValue = values["collateral"];
    fields.forEach((field: any) => {
      if (
        field.name === "LoanDetailsLoanAmount" ||
        field.name === "collateral" ||
        (collateralValue === "Yes" &&
          (field.name === "collateralType" ||
            field.name === "collateralAmount"))
      ) {
        data.push({
          formFieldId: field.id,
          formFieldKey: field.name,
          formFieldValue: values[field.name] ?? "",
        });
      }
    });
    // Build payload for createApplicant API
    const payloadObject = {
      data,
      trackingId: trackingId,
      templateId: templateId,
      formName: horizontalSteps?.[activeHorizontalIndex]?.stepLabel,
      mobileNumber: values?.phone_number,
    };

    try {
      dispatch(updateFormData(values));
      dispatch(setActiveHorizontalIndex(activeHorizontalIndex + 1));
      // await applicantDetailsServices
      //   .createApplicant(payloadObject)
      //   .then(() => {

      //     trackCustomerJourneyEventAndWait(
      //       `${handleGAProvisionalOfferStatments}_LOANDETAILS_PROVISIONALOFFER_PAGE10`,
      //       decrypt(userProfile?.id) ?? "",
      //       trackingId,
      //       {
      //         label: `${handleGAProvisionalOfferStatments}_LOANDETAILS_PROVISIONALOFFER_PAGE10`,
      //         category: `${handleGAProvisionalOfferStatments}_LOANDETAILS_PROVISIONALOFFER_PAGE10`,
      //         action: `${handleGAProvisionalOfferStatments}_LOANDETAILS_PROVISIONALOFFER_PAGE10`,
      //         page: "ProvisionalOff_Loan Details_Provisional Offer",
      //       }
      //     );

      //     dispatch(setActiveHorizontalIndex(activeHorizontalIndex + 1));
      //     // dispatch(nextVerticalStep());
      //   })
      //   .catch((err) => {
      //     const apiErrorMessage = err.response?.data?.message || "";
      //     let displayMessage = "Something went wrong while saving data.";
      //     if (apiErrorMessage.includes("LoanDetailsLoanAmount")) {
      //       displayMessage = "Please enter a loan amount that matches our lending policy to proceed.";
      //     } else if (apiErrorMessage) {
      //       displayMessage = apiErrorMessage;
      //     }

      //     setSnackbar({
      //       open: true,
      //       message: displayMessage,
      //       severity: "error",
      //     });
      //   });
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Something went wrong while saving data.",
        severity: "error",
      });
    }
  };

  const hasRemainingUnsavedBlankCoApplicant = (removedCoAppNum?: string | null) => {
    const allFields = Array.isArray(fields)
      ? fields[0]?.fields
        ? fields.flatMap((g: any) => g.fields)
        : fields
      : [];
    const values = formikRef?.current?.values || {};
    const coApplicantNums = new Set<string>();

    allFields.forEach((field: any) => {
      const match = field?.name?.match(/^(basic|address|employment)CoApplicant(\d+)/);
      if (match && match[2] !== removedCoAppNum) {
        coApplicantNums.add(match[2]);
      }
    });

    return Array.from(coApplicantNums).some((num) => {
      if (savedBasicCoApplicants?.[`CoApplicant${num}`]) return false;
      return allFields.some((field: any) => {
        if (
          !field?.required ||
          !field?.name?.startsWith(`basicCoApplicant${num}`)
        ) {
          return false;
        }
        const value = values[field.name];
        return value === undefined || value === null || value === "";
      });
    });
  };

  useEffect(() => {
    if (!formikRef?.current || !formikRef?.current?.values) return;

    const currentFieldNames = fields?.map((field: any) => field?.name);
    const allValues = { ...formikRef?.current?.values };

    // Find all co-applicant keys in Formik
    const coApplicantKeys = Object.keys(allValues).filter((key) =>
      key.startsWith("coApplicant")
    );

    // Find co-applicant keys that are NOT in the current fields
    const keysToRemove = coApplicantKeys.filter(
      (key) => !currentFieldNames.includes(key)
    );

    // Remove only those keys
    keysToRemove.forEach((key) => {
      delete allValues[key];
    });

    // Add any missing current co-applicant fields with default ""
    currentFieldNames.forEach((name: any) => {
      if (!(name in allValues)) {
        // Set employmentCoApplicantNisEmployed to 'Yes' by default
        if (/^employmentCoApplicant\d+isEmployed$/.test(name)) {
          allValues[name] = "Yes";
        } else if (/^addressCoApplicant\d+currentsameAsPermanent$/.test(name)) {
          allValues[name] = true;
        } else {
          allValues[name] = "";
        }
      } else if (/^addressCoApplicant\d+currentsameAsPermanent$/.test(name)) {
        // Always coerce to boolean, never string
        allValues[name] = allValues[name] === true;
      }
    });

    // Always set basicCoApplicant1PayParty to 'Yes' for co-applicant 1
    if (currentFieldNames.includes("basicCoApplicant1PayParty")) {
      allValues["basicCoApplicant1PayParty"] = "Yes";
    }

    formikRef.current.setValues(allValues, false);
  }, [fields]);

  // Only set addressCoApplicantNcurrentsameAsPermanent to true if undefined (not if user unchecked it)
  useEffect(() => {
    if (formikRef?.current) {
      const allFieldNames = Object.keys(formikRef?.current?.values || {});
      const coApplicantFields = allFieldNames.filter((name) =>
        name.match(/^addressCoApplicant(\d+)currentsameAsPermanent$/)
      );
      coApplicantFields.forEach((fieldName) => {
        const value = formikRef?.current?.values[fieldName];
        if (typeof value === "undefined" || value === "" || value === null) {
          formikRef?.current.setFieldValue(fieldName, true, false);
        }
      });
    }
  }, [fields]);

  useEffect(() => {
    if (formikRef?.current) {
      const values = formikRef?.current?.values || {};
      let shouldUpdate = false;
      if (Array.isArray(fields)) {
        fields.forEach((field) => {
          if (
            /^employmentCoApplicant\d+isEmployed$/.test(field.name) &&
            (!values[field.name] || values[field.name] === "")
          ) {
            formikRef?.current?.setFieldValue(field.name, "Yes", false);
            shouldUpdate = true;
          }
        });
      }
    }
  }, [fields, formikRef]);

  useEffect(() => {
    if (!formikRef?.current) {
      return;
    }
    const values = formikRef.current.values || {};
    Object.keys(values).forEach((key) => {
      const match = getRelationValueFieldMatch(key);
      if (!match) {
        return;
      }
      if (!hasVisibleRelationValueField(match[1]) && values[key]) {
        formikRef.current.setFieldValue(key, "", false);
      }
    });
  }, [fields, formikRef?.current?.values]);

  // const getCoApplicantCount = (data: any) => {
  //   const coApplicantNums = new Set();
    
  //   Object.keys(data).forEach(key => {
  //     // Regex matches "CoApplicant" followed by one or more digits
  //     const match = key.match(/CoApplicant(\d+)/);
  //     if (match) {
  //       coApplicantNums.add(match[1]);
  //     }
  //   });

  //   return coApplicantNums.size;
  // };


  useEffect(() => {
    if (formikRef?.current) {
      const values = formikRef.current.values || {}
      const i = selectedCoApplicantIndex;

      const copiedValues: Record<string, any> = {};
      const copyAddressValue = (fieldName: string, val: any) => {
        copiedValues[fieldName] = val;
      };

        if(values[`addressCoApplicant${i}SameAs`] === "Applicant") {
          const permanentOwnershipStatus = values.permanentOwnershipStatus;
          const permanentYearsInCurrentAddress = values.permanentYearsInCurrentAddress;
          const permanentYearsInCurrentCity = values.permanentYearsInCurrentCity;
          const permanentAddressLine1 = values.permanentAddressLine1;
          const permanentAddressLine2 = values.permanentAddressLine2;
          const permanentAddressLine3 = values.permanentAddressLine3;
          const permanentPincode = values.permanentPincode;
          const permanentCity = values.permanentCity;
          const permanentState = values.permanentState;
          const permanentCountry = values.permanentCountry;
          copyAddressValue(`addressCoApplicant${i}permanentAddressLine1`, permanentAddressLine1);
          copyAddressValue(`addressCoApplicant${i}permanentAddressLine2`, permanentAddressLine2);
          copyAddressValue(`addressCoApplicant${i}permanentAddressLine3`, permanentAddressLine3);
          copyAddressValue(`addressCoApplicant${i}permanentPincode`, permanentPincode);
          copyAddressValue(`addressCoApplicant${i}permanentCity`, permanentCity);
          copyAddressValue(`addressCoApplicant${i}permanentState`, permanentState);
          copyAddressValue(`addressCoApplicant${i}permanentCountry`, permanentCountry);
          copyAddressValue(`addressCoApplicant${i}permanentOwnershipStatus`, permanentOwnershipStatus);
          copyAddressValue(`addressCoApplicant${i}permanentYearsInCurrentAddress`, permanentYearsInCurrentAddress);
          copyAddressValue(`addressCoApplicant${i}permanentYearsInCurrentCity`, permanentYearsInCurrentCity);

          const currentAddressLine1 = values.currentAddressLine1;
          const currentAddressLine2 = values.currentAddressLine2;
          const currentAddressLine3 = values.currentAddressLine3;
          const currentCity = values.currentCity;
          const currentCountry = values.currentCountry;
          const currentOwnershipStatus = values.currentOwnershipStatus;
          const currentPincode = values.currentPincode;
          const currentState = values.currentState;
          const currentYearsInCurrentAddress = values.currentYearsInCurrentAddress;
          const currentYearsInCurrentCity = values.currentYearsInCurrentCity;

          const currentsameAsPermanent = values.currentsameAsPermanent;
          const addressCoApplicantcurrentsameAsPermanent = values[`addressCoApplicant${i}currentsameAsPermanent`];

          copyAddressValue(`addressCoApplicant${i}currentAddressLine1`, currentAddressLine1);
          copyAddressValue(`addressCoApplicant${i}currentAddressLine2`, currentAddressLine2);
          copyAddressValue(`addressCoApplicant${i}currentAddressLine3`, currentAddressLine3);
          copyAddressValue(`addressCoApplicant${i}currentCity`, currentCity);
          copyAddressValue(`addressCoApplicant${i}currentCountry`, currentCountry);
          copyAddressValue(`addressCoApplicant${i}currentOwnershipStatus`, currentOwnershipStatus);
          copyAddressValue(`addressCoApplicant${i}currentPincode`, currentPincode);
          copyAddressValue(`addressCoApplicant${i}currentState`, currentState);
          copyAddressValue(`addressCoApplicant${i}currentYearsInCurrentAddress`, currentYearsInCurrentAddress);
          copyAddressValue(`addressCoApplicant${i}currentYearsInCurrentCity`, currentYearsInCurrentCity);
          copyAddressValue(`addressCoApplicant${i}currentsameAsPermanent`, currentsameAsPermanent);
          copyAddressValue(`addressCoApplicant${i}currentsameAsPermanent`, addressCoApplicantcurrentsameAsPermanent);
        } else {
          const addressCoApplicantSameAs = values[`addressCoApplicant${i}SameAs`]; 
          if(addressCoApplicantSameAs) {
            const coAppNumber = Number(addressCoApplicantSameAs.match(/\d+/)[0]);

            

            const addressCoAppPermanentOwnershipStatus = values[`addressCoApplicant${coAppNumber}permanentOwnershipStatus`];
            const addressCoAppPermanentYearsInCurrentAddress = values[`addressCoApplicant${coAppNumber}permanentYearsInCurrentAddress`];
            const addressCoAppPermanentYearsInCurrentCity = values[`addressCoApplicant${coAppNumber}permanentYearsInCurrentCity`];
            const addressCoAppPermanentAddressLine1 = values[`addressCoApplicant${coAppNumber}permanentAddressLine1`];
            const addressCoAppPermanentAddressLine2 = values[`addressCoApplicant${coAppNumber}permanentAddressLine2`];
            const addressCoAppPermanentAddressLine3 = values[`addressCoApplicant${coAppNumber}permanentAddressLine3`];
            const addressCoAppPermanentPincode = values[`addressCoApplicant${coAppNumber}permanentPincode`];
            const addressCoAppPermanentCity = values[`addressCoApplicant${coAppNumber}permanentCity`];
            const addressCoAppPermanentState = values[`addressCoApplicant${coAppNumber}permanentState`];
            const addressCoAppPermanentCountry = values[`addressCoApplicant${coAppNumber}permanentCountry`];

            copyAddressValue(`addressCoApplicant${i}permanentAddressLine1`, addressCoAppPermanentAddressLine1);
            copyAddressValue(`addressCoApplicant${i}permanentAddressLine2`, addressCoAppPermanentAddressLine2);
            copyAddressValue(`addressCoApplicant${i}permanentAddressLine3`, addressCoAppPermanentAddressLine3);
            copyAddressValue(`addressCoApplicant${i}permanentPincode`, addressCoAppPermanentPincode);
            copyAddressValue(`addressCoApplicant${i}permanentCity`, addressCoAppPermanentCity);
            copyAddressValue(`addressCoApplicant${i}permanentState`, addressCoAppPermanentState);
            copyAddressValue(`addressCoApplicant${i}permanentCountry`, addressCoAppPermanentCountry);
            copyAddressValue(`addressCoApplicant${i}permanentOwnershipStatus`, addressCoAppPermanentOwnershipStatus);
            copyAddressValue(`addressCoApplicant${i}permanentYearsInCurrentAddress`, addressCoAppPermanentYearsInCurrentAddress);
            copyAddressValue(`addressCoApplicant${i}permanentYearsInCurrentCity`, addressCoAppPermanentYearsInCurrentCity);

            const addressCoAppCurrentAddressLine1 = values[`addressCoApplicant${coAppNumber}currentAddressLine1`];
            const addressCoAppCurrentAddressLine2 = values[`addressCoApplicant${coAppNumber}currentAddressLine2`];
            const addressCoAppCurrentAddressLine3 = values[`addressCoApplicant${coAppNumber}currentAddressLine3`];
            const addressCoAppCurrentCity = values[`addressCoApplicant${coAppNumber}currentCity`];
            const addressCoAppCurrentCountry = values[`addressCoApplicant${coAppNumber}currentCountry`];
            const addressCoAppCurrentOwnershipStatus = values[`addressCoApplicant${coAppNumber}currentOwnershipStatus`];
            const addressCoAppCurrentPincode = values[`addressCoApplicant${coAppNumber}currentPincode`];
            const addressCoAppCurrentState = values[`addressCoApplicant${coAppNumber}currentState`];
            const addressCoAppCurrentYearsInCurrentAddress = values[`addressCoApplicant${coAppNumber}currentYearsInCurrentAddress`];
            const addressCoAppCurrentYearsInCurrentCity = values[`addressCoApplicant${coAppNumber}currentYearsInCurrentCity`];
            const addressCoAppCurrentsameAsPermanent = values[`addressCoApplicant${coAppNumber}currentsameAsPermanent`];

            copyAddressValue(`addressCoApplicant${i}currentAddressLine1`, addressCoAppCurrentAddressLine1);
            copyAddressValue(`addressCoApplicant${i}currentAddressLine2`, addressCoAppCurrentAddressLine2);
            copyAddressValue(`addressCoApplicant${i}currentAddressLine3`, addressCoAppCurrentAddressLine3);
            copyAddressValue(`addressCoApplicant${i}currentCity`, addressCoAppCurrentCity);
            copyAddressValue(`addressCoApplicant${i}currentCountry`, addressCoAppCurrentCountry);
            copyAddressValue(`addressCoApplicant${i}currentOwnershipStatus`, addressCoAppCurrentOwnershipStatus);
            copyAddressValue(`addressCoApplicant${i}currentPincode`, addressCoAppCurrentPincode);
            copyAddressValue(`addressCoApplicant${i}currentState`, addressCoAppCurrentState);
            copyAddressValue(`addressCoApplicant${i}currentYearsInCurrentAddress`, addressCoAppCurrentYearsInCurrentAddress);
            copyAddressValue(`addressCoApplicant${i}currentYearsInCurrentCity`, addressCoAppCurrentYearsInCurrentCity);
            copyAddressValue(`addressCoApplicant${i}currentsameAsPermanent`, addressCoAppCurrentsameAsPermanent);
          }
      }

      if (Object.keys(copiedValues).length > 0) {
        const nextValues = { ...values, ...copiedValues };
        // Apply the whole copy in one update, then validate against the explicit
        // final values. Passing nextValues to validateForm means it never has to
        // wait for React to flush the state update above — reading Formik's own
        // state here would still see the pre-copy values and re-report the very
        // "required" errors the copy just resolved.
        formikRef.current.setValues(nextValues, false);
        formikRef.current.validateForm(nextValues);
      }
    }
  }, [formikRef?.current?.values[`addressCoApplicant${selectedCoApplicantIndex}SameAs`]]);

  useEffect(() => {
    if (!formikRef?.current || !selectedCoApplicantIndex) return;

    const { values, setFieldValue } = formikRef.current;
    const prefix = `addressCoApplicant${selectedCoApplicantIndex}`;
    const sameAsKey = `${prefix}SameAs`;

    const currentSameAsValue = values[sameAsKey];
    const previousSameAsValue = prevSameAsValueRef.current;

    const targetFields = targetAddressFields(prefix);
    const targetFieldsChanged = targetFields.some((field) => {
      return prevValuesRef.current[field] !== values[field];
    });

    const sameAsChanged = previousSameAsValue !== currentSameAsValue;
    const selectedIndexChanged = prevSelectedCoApplicantIndexRef.current !== selectedCoApplicantIndex;

    if (selectedIndexChanged) {
      prevSelectedCoApplicantIndexRef.current = selectedCoApplicantIndex;
      prevValuesRef.current = { ...values };
      prevSameAsValueRef.current = currentSameAsValue;
      skipResetAfterSameAsRef.current = false;
      return;
    }

    if (sameAsChanged) {
      skipResetAfterSameAsRef.current = true;
    }

    if (targetFieldsChanged && !skipResetAfterSameAsRef.current && values[sameAsKey]) {
      setFieldValue(sameAsKey, "");
      // Redux formData still holds the old SameAs selection, and DynamicForm's
      // "hydrate Formik from Redux formData" effect resurrects any blank
      // Formik field from a non-blank Redux value on the very next keystroke
      // (any field's onChange dispatches updateFormData). Without this, that
      // resurrected SameAs value re-triggers the copy effect above and stomps
      // whatever the user just typed, one keystroke after this reset fires.
      dispatch(updateFormData({ [sameAsKey]: "" }));
    }

    if (!sameAsChanged) {
      skipResetAfterSameAsRef.current = false;
    }

    prevValuesRef.current = { ...values };
    prevSameAsValueRef.current = currentSameAsValue;
  }, [formikRef?.current?.values, selectedCoApplicantIndex]);

    

  return (
    <>
      {/* Only Loan Amount and Collateral fields */}
      <Grid container spacing={2} m={0}>
        <Grid item xs={12} md={4}>
          {fields
            .filter((f: any) => f.name === "LoanDetailsLoanAmount")
            .map((field: any) => (
              <React.Fragment key={field.name}>
                {renderField(field)}
              </React.Fragment>
            ))}
        </Grid>
        <Grid item xs={12} md={4}>
          {fields
            .filter((f: any) => f.name === "collateral")
            .map((field: any) => (
              <React.Fragment key={field.name}>
                {renderField(field)}
              </React.Fragment>
            ))}
        </Grid>
        <Grid item xs={12}>
          {/* If collateral is 'Yes', show collateralType and collateralAmount fields side by side and make them required */}
          {(values?.["collateral"] === "Yes" ||
            formikRef?.current?.values?.["collateral"] === "Yes") && (
            <Grid container spacing={2}>
              {fields
                .filter(
                  (f: any) =>
                    f.name === "collateralType" || f.name === "collateralAmount"
                )
                .map((field: any) => {
                  // If collateral is 'Yes', force required true for these fields
                  const fieldWithRequired = {
                    ...field,
                    required: true,
                  };
                  return (
                    <Grid item xs={12} sm={4} key={field.name}>
                      {renderField(fieldWithRequired)}
                    </Grid>
                  );
                })}
            </Grid>
          )}
        </Grid>
      </Grid>

      {/* Co-Applicant Details Header */}
      <Grid container spacing={2} justifyContent="space-between" mx={0} mt={1}>
        <Grid item xs={6}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "15px" }}>
            Co-Applicant Details
          </Typography>
        </Grid>
      </Grid>

      {/* Render each co-applicant in a collapsible Accordion with tabs */}
      {(() => {
        // Group all co-applicant fields by co-applicant number and prefix
        const coApplicantFieldMap: Record<
          string,
          { basic: any[]; address: any[]; employment: any[]; ids: number[] }
        > = {};

        fields.forEach((field: any) => {
          const match = field.name.match(
            /^(basic|address|employment)CoApplicant(\d+)/
          );
          if (match) {
            const prefix = match[1] as "basic" | "address" | "employment";
            const num = match[2];
            const key = `CoApplicant${num}`;
            if (!coApplicantFieldMap[key]) {
              coApplicantFieldMap[key] = {
                basic: [],
                address: [],
                employment: [],
                ids: [],
              };
            }
            coApplicantFieldMap[key][prefix].push(field);
            coApplicantFieldMap[key].ids.push(field.id);
          }
        });

        // Sort co-applicants by number
        const coApplicantKeys = Object.keys(coApplicantFieldMap).sort(
          (a, b) => {
            const aNum = parseInt(a.replace("CoApplicant", ""), 10);
            const bNum = parseInt(b.replace("CoApplicant", ""), 10);
            return aNum - bNum;
          }
        );
  
        // Sync local expanded with Redux (and fallback to localStorage on mount)
        React.useEffect(() => {
          const saved =
            typeof window !== "undefined"
              ? localStorage.getItem("coAppExpandedKey")
              : null;
          if (loanDetailCoApplicantCollapseValue) {
            setExpanded(loanDetailCoApplicantCollapseValue);
             if (loanDetailCoApplicantCollapseValue && loanDetailCoApplicantCollapseValue !== null && typeof loanDetailCoApplicantCollapseValue === "string") {
              const expandedNumber: string | undefined = loanDetailCoApplicantCollapseValue.match(/\d+/)?.[0];
              const number = parseInt(expandedNumber || "", 10);
              setSelectedCoApplicantIndex(number);
            }
          } else if (saved) {
            setExpanded(saved);
            if (saved && saved !== null && typeof saved === "string") {
              const expandedNumber: string | undefined = saved.match(/\d+/)?.[0];
              const number = parseInt(expandedNumber || "", 10);
              setSelectedCoApplicantIndex(number);
            }
          }
        }, [loanDetailCoApplicantCollapseValue]);

        // If the stored expanded key no longer exists (after add/remove), fall back safely
        React.useEffect(() => {
          if (
            expanded &&
            typeof expanded === "string" &&
            !coApplicantKeys.includes(expanded)
          ) {
            const fallback =
              coApplicantKeys[coApplicantKeys.length - 1] || "CoApplicant1";
            
            //setExpanded(fallback);
            if (typeof window !== "undefined") {
              //localStorage.setItem("coAppExpandedKey", fallback);
            }
            // dispatch(setLoanDetailCoApplicantCollapseValue(fallback));
          }
        }, [coApplicantKeys, expanded]);

        // const [tabIndex, setTabIndex] = React.useState<Record<string, number>>(
        //   {}
        // );
        // Track enabled tabs per co-applicant: { [coAppKey]: [true, false, false] }
        const [tabEnabled, setTabEnabled] = React.useState<
          Record<string, boolean[]>
        >({});

        // Initialize/recompute tabEnabled for each co-applicant on every
        // values change. The `if (!tabEnabled[coAppKey])` guard this used to
        // have only ever computed a co-applicant's tab-enabled state ONCE
        // (typically while its fields were still empty), then never again —
        // so a co-applicant whose data was actually complete (e.g. restored
        // from a fetch after logging back into the same lead) could still
        // show Address/Employment as permanently disabled.
        React.useEffect(() => {
          setTabEnabled((prev) => {
            const next = { ...prev };
            coApplicantKeys.forEach((coAppKey) => {
              // For each tab, check if all required fields are filled
              const tabStates = [0, 1, 2].map((tabIdx) => {
                const tabFieldsArr = [
                  coApplicantFieldMap[coAppKey].basic,
                  coApplicantFieldMap[coAppKey].address,
                  coApplicantFieldMap[coAppKey].employment,
                ];
                const requiredFields = tabFieldsArr[tabIdx].filter((f) => {
                  // For employment sub-fields, skip if isEmployed is 'No'; if Others/Farmer, only keep the required trio.
                  const empFieldMatch = f.name.match(
                    /^employmentCoApplicant(\d+)/
                  );
                  if (empFieldMatch) {
                    const coAppNum = empFieldMatch[1];
                    const isEmployedFieldName = `employmentCoApplicant${coAppNum}isEmployed`;
                    const employmentTypeFieldName = `employmentCoApplicant${coAppNum}employmentType`;
                    const isEmployed =
                      formikRef?.current?.values?.[isEmployedFieldName];
                    const selectedEmploymentType = getAutocompleteOptionOrValue(
                      formikRef?.current?.values?.[employmentTypeFieldName],
                    );
                    if (isEmployed === "No" && f.name !== isEmployedFieldName) {
                      return false;
                    }
                    if (
                      isEmployed === "Yes" &&
                      !selectedEmploymentType &&
                      f.name !== employmentTypeFieldName
                    ) {
                      return false;
                    }
                    if (
                      isEmployed === "Yes" &&
                      (selectedEmploymentType === "Others" ||
                        selectedEmploymentType === "Farmer") &&
                      f.name !== employmentTypeFieldName &&
                      f.name !==
                        `employmentCoApplicant${coAppNum}monthlyObligation` &&
                      f.name !== `employmentCoApplicant${coAppNum}monthlyIncome`
                    ) {
                      return false;
                    }
                  }
                  return f.required;
                });
                return requiredFields.every((f) => {
                  const val = formikRef?.current?.values?.[f.name];
                  return val !== undefined && val !== null && val !== "";
                });
              });
              // Always enable the first tab, others only if their required fields are filled
              next[coAppKey] = [true, ...tabStates.slice(1)];
            });
            return next;
          });
        }, [coApplicantKeys, formikRef?.current?.values]);

        // Keep "Add More" in sync with the last co-applicant's actual
        // completion state (tabEnabled, computed above from current form
        // values) instead of only a one-off dispatch after a save action.
        // disableAddMoreCoApplicant defaults to `true` in Redux, so on a
        // fresh page load/re-login for a lead whose co-applicants are
        // already fully filled in, nothing was re-evaluating it against the
        // restored data — the button stayed disabled even though the data
        // was actually complete.
        React.useEffect(() => {
          if (coApplicantKeys.length === 0) return;
          const lastCoAppKey = coApplicantKeys[coApplicantKeys.length - 1];
          const lastCoApplicantTabs = tabEnabled[lastCoAppKey];
          if (!lastCoApplicantTabs) return;
          const isLastCoApplicantComplete = lastCoApplicantTabs.every(Boolean);
          if (disableAddMoreCoApplicant === !isLastCoApplicantComplete) return;
          dispatch(setDisableAddMoreCoApplicant(!isLastCoApplicantComplete));
        }, [tabEnabled, coApplicantKeys, disableAddMoreCoApplicant, dispatch]);

        const handleAccordionChange = (panel: string) => () => {
          const nextExpanded = expanded === panel ? false : panel;
          if (nextExpanded && typeof window !== "undefined") {
            localStorage.setItem("coAppExpandedKey", nextExpanded as string);
          } else if (typeof window !== "undefined") {
            localStorage.removeItem("coAppExpandedKey");
          }
          dispatch(
            setLoanDetailCoApplicantCollapseValue(
              typeof nextExpanded === "string" ? nextExpanded : panel
            )
          );
          setExpanded(nextExpanded);
          if (expanded && expanded !== null && typeof expanded === "string") {
              const expandedNumber: string | undefined = expanded.match(/\d+/)?.[0];
              const number = parseInt(expandedNumber || "", 10);
              setSelectedCoApplicantIndex(number);
          }
         
        };

        const handleZipCode = (coAppKey: any) => {
          const zipCodePermanentVal =
            formData?.[
            `addressCoApplicant${coAppKey.split("CoApplicant")?.[1]
            }permanentPincode`
            ];

          const zipCodeCurrentVal =
            formData?.[
            `addressCoApplicant${coAppKey.split("CoApplicant")?.[1]
            }currentPincode`
            ];

          // Only call API if pincode exists and is 6 digits
          if (zipCodePermanentVal && zipCodePermanentVal.length === 6) {
            handleInvokZipCode(
              zipCodePermanentVal,
              coAppKey.split("CoApplicant")?.[1],
              "permanent"
            );
          }

          if (zipCodeCurrentVal && zipCodeCurrentVal.length === 6) {
            handleInvokZipCode(
              zipCodeCurrentVal,
              coAppKey.split("CoApplicant")?.[1],
              "current"
            );
          }
        };

        const handleTabChange = (coAppKey: string, newIndex: number) => {
          if (newIndex == 1) {
            handleZipCode(coAppKey);
          }
          // Only allow tab change if tab is enabled
          if (tabEnabled[coAppKey]?.[newIndex]) {
            setTabIndex((prev: any) => ({ ...prev, [coAppKey]: newIndex }));
          }
        };
        // --- Save & Next/Save button handlers ---
        const handleTabSave = (
          coAppKey: string,
          tabIdx: number,
          isLast: boolean
        ) => {
          // Save current co-applicant's fields (all tabs) + LoanDetailsLoanAmount/collateral
          (async () => {
            setIsSaveNextLoading(true);
            const formData: any[] = [];
            const coAppNum = coAppKey.replace("CoApplicant", "");

            const values = formikRef?.current?.values || {};
            fields.forEach((field: any) => {
              if (field.name.includes(`CoApplicant${coAppNum}`)) {
                // Skip AadhaarNumber field
                // if (field.name === `basicCoApplicant${coAppNum}AadhaarNumber`) {
                //   return;
                // }
                const rawFieldValue = values[field.name] ?? "";
                const isBirthCityField = /city.*birth|birth.*city/i.test(
                  String(field?.label || "") + String(field?.name || ""),
                );
                const isNationalityField = /^basicCoApplicant\d+Nationality$/.test(
                  String(field?.name || ""),
                );
                const isMaritalStatusField = /^basicCoApplicant\d+MaritalStatus$/.test(
                  String(field?.name || ""),
                );
                const isCountryOfBirthField = /^basicCoApplicant\d+CountryOfBirth$/.test(
                  String(field?.name || ""),
                );
                // save-coapplicant (local API) always expects the display
                // name, never the master-data code — resolve against the
                // matching options list regardless of whether Formik/Redux
                // currently holds the code or the label for this field, so
                // it's consistent whichever journey (DIY/Summary) set it.
                let fieldValue: any;
                if (isMaritalStatusField) {
                  fieldValue =
                    resolveAutocompleteOption(rawFieldValue, maritalStatusOptions)?.label ??
                    getAutocompleteOptionValue(rawFieldValue);
                } else if (isCountryOfBirthField) {
                  fieldValue =
                    resolveAutocompleteOption(rawFieldValue, countryOptions)?.label ??
                    getAutocompleteOptionValue(rawFieldValue);
                } else if (isNationalityField) {
                  fieldValue =
                    resolveAutocompleteOption(rawFieldValue, nationalityOptions)?.label ??
                    (rawFieldValue && typeof rawFieldValue === "object"
                      ? "label" in rawFieldValue
                        ? rawFieldValue.label
                        : "value" in rawFieldValue
                          ? rawFieldValue.value
                          : rawFieldValue
                      : rawFieldValue);
                } else if (isBirthCityField) {
                  fieldValue =
                    rawFieldValue && typeof rawFieldValue === "object"
                      ? "label" in rawFieldValue
                        ? rawFieldValue.label
                        : "value" in rawFieldValue
                          ? rawFieldValue.value
                          : rawFieldValue
                      : rawFieldValue;
                } else if (/^employmentCoApplicant\d+designation$/i.test(String(field?.name || ""))) {
                  fieldValue = getMasterOptionName(rawFieldValue, dropdownData?.designations);
                } else {
                  fieldValue = rawFieldValue;
                }
                formData.push({
                  formFieldId: field.id,
                  fieldKey: field.name,
                  fieldValue,
                });
              }
            });
            // Add extra keys for API payload
            // Find correct keys for this co-applicant
            const mobileNumber =
              values[`basicCoApplicant${coAppNum}PhoneNumber`] || "";
            const email = values[`basicCoApplicant${coAppNum}Email`] || "";
            const coApplicantType = `Co-Applicant ${coAppNum}`;

            const coAppIndex = String(Number(coAppNum) - 1); // '0' for type 1, '1' for type 2
            const aadhaarSeedingStatus =
              coApplicantVerification?.[coAppIndex]?.aadhaarSeedingStatus;
            const isAadhaarSeeded =
              aadhaarSeedingStatus === undefined ||
                aadhaarSeedingStatus === null ||
                aadhaarSeedingStatus === ""
                ? false
                : aadhaarSeedingStatus;

            let finalIsAadhaarSeeded = isAadhaarSeeded;

            if (finalIsAadhaarSeeded === "") {
              const payload = {
                trackingId: trackingId,
                coApplicantType,
              };
              const resp: any = await losServices.getCoApplicantDetails(
                payload
              );
              // You can extract aadhaarSeedingStatus from resp if available
              const statusFromApi = resp?.data?.data?.isAadhaarSeeded;

              finalIsAadhaarSeeded =
                statusFromApi === undefined ||
                  statusFromApi === null ||
                  statusFromApi === ""
                  ? ""
                  : statusFromApi;
            }
            try {
              const shouldAutoInvokeLeadFlow: boolean =
                checkIsLeadApiInvoked.ASSIGN_SM == false ||
                checkIsLeadApiInvoked.CREATE_LEAD == false ||
                checkIsLeadApiInvoked.LEAD_TO_APPLICATION == false;
              if (shouldAutoInvokeLeadFlow) {
                handleLeadCreationFlow(getLeadPayload)
              }
              dispatch(updateFormData(values));
              let section="basic";
              if(tabIdx===1){
                section="address";
              }else if(tabIdx===2){
                section="employment";
              }
              // idDto: resolved-ID mirror of the raw `formData` above, for
              // this save-coapplicant call into OUR OWN DB (not the
              // processio/LOS calls further down, which build their own
              // payloads independently). See buildCoApplicantSaveIdDto for
              // why: built entirely from what's already synchronously
              // available (dropdownData lookups, coApplicantZipCodeData) —
              // no new API calls, no reordering. Shared across
              // CoApplicantDetailsSection/CoApplicantAdditionalDetails/
              // FormEdit instead of each carrying its own copy.
              const birthCountryLabelForDto =
                resolveAutocompleteOption(
                  values[`basicCoApplicant${coAppNum}CountryOfBirth`],
                  countryOptions,
                )?.label ||
                getAutocompleteOptionValue(
                  values[`basicCoApplicant${coAppNum}CountryOfBirth`],
                );
              const idDtoFieldPrefixes: Record<string, string> = {
                basic: `basicCoApplicant${coAppNum}`,
                permanent: `addressCoApplicant${coAppNum}permanent`,
                current: `addressCoApplicant${coAppNum}current`,
                employment: `employmentCoApplicant${coAppNum}`,
                company: `employmentCoApplicant${coAppNum}company`,
              };
              // birthCityId is mandatory downstream, so this needs to be
              // more than a cache-hit check: getCityOptionsForCountry can
              // come back non-empty yet still not contain the specific city
              // this co-applicant selected (e.g. a stale/partial cache from
              // an earlier country), which would silently resolve to blank
              // without ever attempting a fetch. Retry with a fresh fetch
              // whenever the cached list doesn't actually resolve this raw
              // city value — not just when it's empty — same
              // fetchCitiesByCountryState call the (unconditional,
              // same-values) basic-details ID block further down already
              // makes for the LOS payload's own birthCityId.
              const rawBirthCityForDto = values[`basicCoApplicant${coAppNum}CityOfBirth`];
              let cityOptionsForBirthForDto = getCityOptionsForCountry(
                birthCountryLabelForDto,
              );
              if (
                birthCountryLabelForDto &&
                !resolveBirthCityId(rawBirthCityForDto, cityOptionsForBirthForDto)
              ) {
                cityOptionsForBirthForDto = await fetchCitiesByCountryState(
                  birthCountryLabelForDto,
                );
              }
              const idDto = buildCoApplicantSaveIdDto({
                getField: (fieldSection, suffix) =>
                  values[`${idDtoFieldPrefixes[fieldSection]}${suffix}`],
                stage: section as "basic" | "address" | "employment",
                countryOptions,
                maritalStatusOptions,
                nationalityOptions,
                cityOptionsForBirth: cityOptionsForBirthForDto,
                coApplicantZipCodeData,
                coAppKey,
                designations: dropdownData?.designations,
              });
              let res = await postsServices.addPost(
                "api/v1/applicant/save-coapplicant",
                {
                  trackingId,
                  mobileNumber,
                  coApplicantType,
                  email: email.toLowerCase(),
                  isAadhaarSeeded:
                    isAadhaarSeeded === "" ||
                      isAadhaarSeeded === null ||
                      isAadhaarSeeded === undefined
                      ? finalIsAadhaarSeeded
                      : isAadhaarSeeded,
                  formData,
                  section:section,
                  idDto,
                }
              );
              if (tabIdx == 0) {
                
                const coAppPanFieldValue =
                  res?.data?.data?.formData?.find(
                    (item: any) =>
                      item.fieldKey === `basicCoApplicant${coAppNum}PanNumber`
                  )?.fieldValue;
                if (coAppPanFieldValue) {
                  const coAppPanApplicationId =
                    Number(res?.data?.data?.applicationId) ||
                    Number(applicationId);
                  if (coAppPanApplicationId > 0) {
                    const piiPublicKey = await fetchSanitizedPiiPublicKey();
                    
                  }
                }
              }
              setSnackbar({
                open: true,
                message: "Co-applicant details for the tab saved successfully.",
                severity: "success",
              });
              const payload = {
                trackingId: trackingId,
                coApplicantType: `Co-Applicant ${coAppNum}`,
              };
              let applicantIdValue = "";
              const resp: any = await losServices.getCoApplicantDetails(
                payload
              );

              if (tabIdx === 0 && res?.status === 200 || res?.status === 201) {
                dispatch(
                  setSavedBasicForCoApplicant({
                    coAppKey, // e.g. "CoApplicant1"
                    value: true,
                  })
                );
              }

              if (tabIdx === 2 && resp?.status === 200) {
                setExpanded(false);
                if (typeof window !== "undefined") {
                  localStorage.removeItem("coAppExpandedKey");
                }
                dispatch(setLoanDetailCoApplicantCollapseValue(""));
                updateAddMoreStateFromCoApplicantResp(resp);
              }

              if (resp.status == 200)
                applicantIdValue = resp?.data?.data?.applicantId;

              // On success, enable next tab for this co-applicant (if not last)
              if (!isLast) {
                setTabEnabled((prev) => {
                  const arr = prev[coAppKey]
                    ? [...prev[coAppKey]]
                    : [true, false, false];
                  arr[tabIdx + 1] = true;
                  return { ...prev, [coAppKey]: arr };
                });
                setTabIndex((prev: any) => ({ ...prev, [coAppKey]: tabIdx + 1 }));
              }

              if (tabIdx == 0) {
                // Call handleZipCode after saving basic details to fetch pincode data
                handleZipCode(coAppKey);
              }

              // Gender can be stored as "male" (dynamic form options), "Male"
              // (AddNewCoApplicantModal options) or a code like "M", so compare
              // case-insensitively against both name and code.
              const genderRaw = String(
                values[`basicCoApplicant${coAppNum}Gender`] ?? ""
              ).trim().toLowerCase();
              let genderId =
                mapGender().find(
                  (item: any) =>
                    item.name?.toLowerCase() === genderRaw ||
                    item.code?.toLowerCase() === genderRaw
                )?.code || "";
              const birthCountryId =
                resolveAutocompleteOption(
                  values[`basicCoApplicant${coAppNum}CountryOfBirth`],
                  countryOptions,
                )?.value || "";
              const birthCountryLabel =
                resolveAutocompleteOption(
                  values[`basicCoApplicant${coAppNum}CountryOfBirth`],
                  countryOptions,
                )?.label ||
                getAutocompleteOptionValue(
                  values[`basicCoApplicant${coAppNum}CountryOfBirth`],
                );
              let cityOptionsForBirth = getCityOptionsForCountry(birthCountryLabel);
              if (!cityOptionsForBirth.length && birthCountryLabel) {
                cityOptionsForBirth = await fetchCitiesByCountryState(
                  birthCountryLabel,
                );
              }
              const birthCityId = resolveBirthCityId(
                values[`basicCoApplicant${coAppNum}CityOfBirth`],
                cityOptionsForBirth,
              );

              if (tabIdx === 0 && res?.data?.leadId) {
                if (applicantIdValue) {
                  trackCustomerJourneyEvent(
                    `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}BASIC_SAVE_PAGE10`,
                    decrypt(userProfile?.id) ?? "",
                    trackingId,
                    {
                      label: `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}BASIC_SAVE_PAGE10`,
                      category: `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}BASIC_SAVE_PAGE10`,
                      action: `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}BASIC_SAVE_PAGE10`,
                      page: `ProvisionalOff_Loan Details_COAPP(${coAppNum}) Basic Details`,
                    }
                  );
                } else {  
                  if (Number(applicationId) > 0) {
                    trackCustomerJourneyEvent(
                      `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}BASIC_SAVE_PAGE10`,
                      decrypt(userProfile?.id) ?? "",
                      trackingId,
                      {
                        label: `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}BASIC_SAVE_PAGE10`,
                        category: `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}BASIC_SAVE_PAGE10`,
                        action: `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}BASIC_SAVE_PAGE10`,
                        page: `ProvisionalOff_Loan Details_COAPP(${coAppNum}) Basic Details`,
                      }
                    );
                  }
                }
              }
              if (tabIdx === 1) {

                trackCustomerJourneyEventAndWait(
                  `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}ADDRESS_SAVE_PAGE10`,
                  decrypt(userProfile?.id) ?? "",
                  trackingId,
                  {
                    label: `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}ADDRESS_SAVE_PAGE10`,
                    category: `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}ADDRESS_SAVE_PAGE10`,
                    action: `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}ADDRESS_SAVE_PAGE10`,
                    page: `ProvisionalOff_Loan Details_COAPP(${coAppNum}) Address Details`,
                  }
                );
              }
            } catch (err) {
              console.error("Error saving co-applicant:", err);
            } finally {
              setIsSaveNextLoading(false);
              if (isXsOrSm) {
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }
          })();
        };

        return coApplicantKeys.map((coAppKey, idx) => {
          const group = coApplicantFieldMap[coAppKey];
          const coAppNum = coAppKey.replace("CoApplicant", "");
          const relationValueFieldName = getRelationValueFieldName(coAppNum);
          const basicFields = group.basic.some(
            (field: any) => field.name === relationValueFieldName
          )
            ? group.basic
            : (() => {
              const relationFieldIndex = group.basic.findIndex(
                (field: any) =>
                  field.name === `basicCoApplicant${coAppNum}RelationWithStudent`
              );
              if (relationFieldIndex === -1) {
                return group.basic;
              }
              const relationField = group.basic[relationFieldIndex];
              const syntheticField = {
                ...relationField,
                id: `${relationValueFieldName}-synthetic`,
                label: "Specify Relationship",
                name: relationValueFieldName,
                required: true,
                type: "text",
                options: null,
                fieldOrder: (relationField?.fieldOrder ?? 0) + 0.1,
              };
              const nextBasicFields = [...group.basic];
              nextBasicFields.splice(relationFieldIndex + 1, 0, syntheticField);
              return nextBasicFields;
            })();
          const currentTab = tabIndex[coAppKey] || 0;
          const tabLabels = [
            "Basic Details",
            "Address Details",
            "Employment Details",
          ];
          const sortByFieldOrder = (arr: any[]) =>
            (arr || []).slice().sort((a: any, b: any) => {
              const aOrder = a?.fieldOrder ?? 0;
              const bOrder = b?.fieldOrder ?? 0;
              return aOrder - bOrder;
            });
          const tabFields = [
            sortByFieldOrder(basicFields),
            sortByFieldOrder(group.address),
            sortByFieldOrder(group.employment),
          ];

          return (
            <>
              <Grid xs={12} sx={{ pl: 2, mt: 3 }} className="border-none">
                <CollapsibleSection
                  key={coAppKey}
                  title={`Co-Applicant ${idx + 1}`}
                  isExpanded={expanded === coAppKey}
                  onToggle={handleAccordionChange(coAppKey)}
                  paddingTopTabZero={true}
                >
                  {/* Note: Only for co-applicant 1 */}
                  {idx === 0 && (
                    <Grid
                      item
                      xs={12}
                      sx={{ mb: 1, p: 1 }}
                      style={{
                        backgroundColor: "#EBF9F9",
                        borderRadius: "8px",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 400,
                          fontSize: "15px",
                          color: "#5E596C",
                        }}
                      >
                        Please add the co-applicant whose financial to be
                        consider for loan evaluation
                      </Typography>
                    </Grid>
                  )}
                  {/* Tabs for Basic, Address, Employment */}
                  <Tabs
                    value={currentTab}
                    onChange={(_e: any, newIndex: any) =>
                      handleTabChange(coAppKey, newIndex)
                    }
                    aria-label={`Tabs for ${coAppKey}`}
                    variant={isXsOrSm ? "scrollable" : "standard"}
                    {...(isXsOrSm && {
                      scrollButtons: "auto",
                      allowScrollButtonsMobile: false,
                    })}
                    sx={{
                      flexGrow: 1,
                      minWidth: 0,
                      "& .MuiTabs-indicator": {
                        backgroundColor: "#00b3b5",
                      },
                      "& .MuiTab-root": {
                        textTransform: "none",
                        fontWeight: 600,
                        color: "#616161",
                        "&.Mui-selected": {
                          color: "#00b3b5",
                        },
                      },
                    }}
                  >
                    {tabLabels.map((label, i) => (
                      <Tab
                        key={label}
                        label={label}
                        disabled={!tabEnabled[coAppKey]?.[i]}
                      />
                    ))}
                  </Tabs>
                  <Box sx={{ mt: 2 }}>
                    {tabFields[currentTab].length === 0 ? (
                      <Typography
                        sx={{ mt: 2 }}
                        variant="body2"
                        color="text.secondary"
                      >
                        No fields
                      </Typography>
                    ) : (
                      <>
                        {/* Aadhaar Verification block for Basic Details tab */}
                        {tabLabels[currentTab] === "Basic Details" ? (
                          <>
                            {/* Render all basic fields first */}
                            <Grid container spacing={2}>
                              {tabFields[currentTab]
                                .filter((field: any) => {
                                  // Exclude AadhaarNumber and all IsPanVerified, IsMobileVerified, IsEmailVerified, AadhaarVerified fields for any co-applicant
                                  // Also exclude basicCoApplicant1PayParty for co-applicant 1 only
                                  if (
                                    /^basicCoApplicant\d+AadhaarNumber$/.test(
                                      field.name
                                    ) ||
                                    /^basicCoApplicant\d+IsPanVerified$/.test(
                                      field.name
                                    ) ||
                                    /^basicCoApplicant\d+IsMobileVerified$/.test(
                                      field.name
                                    ) ||
                                    /^basicCoApplicant\d+IsEmailVerified$/.test(
                                      field.name
                                    ) ||
                                    /^basicCoApplicant\d+AadhaarVerified$/.test(
                                      field.name
                                    )
                                  ) {
                                    return false;
                                  }
                                  // Exclude PayParty for co-applicant 1 only
                                  if (
                                    field.name === "basicCoApplicant1PayParty"
                                  ) {
                                    return false;
                                  }
                                  if (
                                    getRelationValueFieldMatch(field.name)
                                  ) {
                                    const relationValueMatch =
                                      getRelationValueFieldMatch(field.name);
                                    return relationValueMatch
                                      ? hasVisibleRelationValueField(
                                        relationValueMatch[1]
                                      )
                                      : false;
                                  }
                                  return true;
                                })
                                ?.sort((prev: any, next: any) => prev?.fieldOrder - next?.fieldOrder)
                                ?.map((field: any) => {
                                  let enrichedField = field;
                                  if (/^basicCoApplicant\d+MaritalStatus$/.test(field.name)) {
                                    enrichedField = { ...field, options: { options: maritalStatusOptions } };
                                  } else if (/^basicCoApplicant\d+Nationality$/.test(field.name) || /naionality/.test(field.name.toLowerCase())) {
                                    enrichedField = { ...field, options: { options: nationalityOptions } };
                                  }
                                  if (getRelationValueFieldMatch(field.name)) {
                                    enrichedField = {
                                      ...enrichedField,
                                      required: isRelationValueFieldRequired(field),
                                    };
                                  }
                                  return (
                                  <Grid
                                    item
                                    xs={12}
                                    sm={
                                      field.name.includes("PayParty") ? 12 : 6
                                    }
                                    md={
                                      field.name.includes("PayParty") ? 12 : 4
                                    }
                                    key={field.name}
                                  >
                                    {renderField(enrichedField)}
                                  </Grid>
                                  );
                                })}
                            </Grid>
                            {/* Aadhaar Verification UI */}
                            <Grid container className="inner-form-box" mt={2}>
                              <Grid item xs={12} sm={12} md={12}>
                                <Box sx={{ display: "flex", gap: 1 }}>
                                  <img
                                    src={idIcon}
                                    alt="icon"
                                    style={{ width: 20, height: 20 }}
                                  />
                                  <Typography
                                    sx={{
                                      fontSize: "13px",
                                      fontWeight: 500,
                                      color: "#4B465C",
                                    }}
                                  >
                                    Verify Aadhaar Number
                                  </Typography>
                                </Box>
                                <label
                                  className="text-lable"
                                  style={{ marginBottom: "10px" }}
                                >
                                  Verify your Aadhaar to enhance processing speed and accuracy. This streamlined step is provided for your ease and remains optional, allowing you to enter details manually if you prefer.
                                </label>
                              </Grid>
                              {isAadharLoading[coAppKey] ? (
                                <Skeleton
                                  variant="rectangular"
                                  width={120}
                                  height={40}
                                  sx={{ borderRadius: 2, mb: 1 }}
                                />
                              ) : (
                                <IconButton
                                  sx={{
                                    backgroundColor:
                                      formikRef?.current?.values?.[
                                        `basicCoApplicant${coAppKey.replace(
                                          "CoApplicant",
                                          ""
                                        )}AadhaarVerified`
                                      ] === true ||
                                        isDocumentVerifiedCoApplicant[coAppKey]
                                        ? "#33c2c4"
                                        : undefined,
                                    color:
                                      formikRef?.current?.values?.[
                                        `basicCoApplicant${coAppKey.replace(
                                          "CoApplicant",
                                          ""
                                        )}AadhaarVerified`
                                      ] === true ||
                                        isDocumentVerifiedCoApplicant[coAppKey]
                                        ? "#33c2c4"
                                        : "#000",
                                    border:
                                      formikRef?.current?.values?.[
                                        `basicCoApplicant${coAppKey.replace(
                                          "CoApplicant",
                                          ""
                                        )}AadhaarVerified`
                                      ] === true ||
                                        isDocumentVerifiedCoApplicant[coAppKey]
                                        ? "1px solid #33c2c4"
                                        : "",
                                    "&.Mui-disabled": {
                                      backgroundColor: formikRef?.current?.values?.[
                                        `basicCoApplicant${coAppKey.replace(
                                          "CoApplicant",
                                          ""
                                        )}AadhaarVerified`
                                      ] === true ||
                                        isDocumentVerifiedCoApplicant[coAppKey]
                                        ? "white !important"
                                        : "lightgray !important",
                                      color:
                                        formikRef?.current?.values?.[
                                          `basicCoApplicant${coAppKey.replace(
                                            "CoApplicant",
                                            ""
                                          )}AadhaarVerified`
                                        ] === true ||
                                          isDocumentVerifiedCoApplicant[coAppKey]
                                          ? "#33c2c4"
                                          : "#000",
                                    },
                                  }}
                                  onClick={async () => {
                                    // Find co-applicant values for this tab
                                    const coAppNum = coAppKey.replace(
                                      "CoApplicant",
                                      ""
                                    );
                                    const values =
                                      formikRef?.current?.values || {};
                                    const getField = (suffix: string) =>
                                      values[
                                      `basicCoApplicant${coAppNum}${suffix}`
                                      ] || "";
                                    const email = getField("Email");
                                    const firstName = getField("FirstName");
                                    const lastName = getField("LastName");
                                    const phone_number =
                                      getField("PhoneNumber");
                                    setIsAadharLoading((prev) => ({
                                      ...prev,
                                      [coAppKey]: true,
                                    }));
                                    try {

                                      const panNumber = getField("PanNumber");
                                      const aadhaarPayload: any = {
                                        applicantEmail: email?.toLowerCase(),
                                        applicantName: `${firstName || ""} ${lastName || ""
                                          }`.trim(),
                                        applicantMobile: phone_number,
                                        applicantPan: panNumber,
                                        redirectUrl: `${window.location.origin}/application/apply-now`,
                                        landingPageNotRequired: true,
                                        trackingId: trackingId,
                                        typeOfApplicant: `Co-Applicant ${coAppNum}`,
                                      };

                                      const resp = await postsServices.addPost(
                                        "api/v1/applicant/create-app-via-aadhaar-phone",
                                        aadhaarPayload
                                      );

                                      if (
                                        resp.data?.status === "success" &&
                                        resp.data.data?.link
                                      ) {

                                        let link = resp.data.data.link;
                                        const applicationNo =
                                          resp.data.data.applicationNo;
                                        localStorage.setItem(
                                          "applicationNo",
                                          applicationNo
                                        );
                                        localStorage.setItem(
                                          "aadhaarCoAppKey",
                                          coAppKey
                                        );
                                        dispatch(
                                          setAadhaarApplicationNoCoApplicant({
                                            coAppKey,
                                            applicationNo,
                                          })
                                        );
                                        // Optionally: dispatch(setAadhaarApplicationNo(applicationNo));
                                        if (!/^https?:\/\//i.test(link)) {
                                          link = "https://" + link;
                                        }

                                        // Persist all current form values to Redux so they survive the page reload
                                        // after Aadhaar verification journey (nationality, maritalStatus,
                                        // cityOfBirth, countryOfBirth, etc.)
                                        dispatch(updateFormData(values));
                                        // Force Redux persist to flush to sessionStorage before navigating away
                                        await persistor.flush();

                                        await trackCustomerJourneyEventAndWait(
                                          `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}BASIC_AADHAARVERI_PAGE10`,
                                          decrypt(userProfile?.id) ?? "",
                                          trackingId,
                                          {
                                            label: `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}BASIC_AADHAARVERI_PAGE10`,
                                            category: `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}BASIC_AADHAARVERI_PAGE10`,
                                            action: `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}BASIC_AADHAARVERI_PAGE10`,
                                            page: `ProvisionalOff_Loan Details_COAPP(${coAppNum}) Aadhaar Verification`,
                                          },
                                          500
);

                                        // Persist all current co-applicant Formik values to Redux BEFORE navigation
                                        // so that fields like nationality, maritalStatus, cityOfBirth, countryOfBirth
                                        // are not lost on page reload after Aadhaar redirect.
                                        const currentFormValues = formikRef?.current?.values || {};
                                        const coAppFormData: Record<string, any> = {};
                                        Object.keys(currentFormValues).forEach((key) => {
                                          if (key.includes(`CoApplicant${coAppNum}`)) {
                                            coAppFormData[key] = currentFormValues[key];
                                          }
                                        });
                                        if (Object.keys(coAppFormData).length > 0) {
                                          dispatch(updateFormData(coAppFormData));
                                        }

                                        window.location.replace(link);
                                      } else {
                                        setSnackbar({
                                          open: true,
                                          message: resp.data?.message,
                                          severity: "success",
                                        });
                                        // dispatch(
                                        //   setStatusPopup({
                                        //     status: "info",
                                        //     message: resp.data?.message,
                                        //   })
                                        // );
                                      }
                                    } catch (err: any) {
                                      setSnackbar({
                                        open: true,
                                        message: err?.response?.data?.message,
                                        severity: "error",
                                      });
                                      // dispatch(
                                      //   setStatusPopup({
                                      //     status: "info",
                                      //     message: err?.response?.data?.message,
                                      //   })
                                      // );
                                    } finally {
                                      setIsAadharLoading((prev) => ({
                                        ...prev,
                                        [coAppKey]: false,
                                      }));
                                    }
                                  }}
                                  disabled={(() => {
                                    const coAppNum = coAppKey.replace("CoApplicant", "");
                                    // Already verified — disable
                                    const isAadhaarVerified =
                                      formikRef?.current?.values?.[`basicCoApplicant${coAppNum}AadhaarVerified`] === true ||
                                      isDocumentVerifiedCoApplicant[coAppKey];
                                    // PAN must be verified (previous validation)
                                    const isPanNotVerified =
                                      formikRef?.current?.values?.[`basicCoApplicant${coAppNum}IsPanVerified`] === "" ||
                                      formikRef?.current?.values?.[`basicCoApplicant${coAppNum}IsPanVerified`] === false;
                                    // Mobile must be exactly 10 digits (new validation)
                                    const mobileVal: string =
                                      formikRef?.current?.values?.[`basicCoApplicant${coAppNum}PhoneNumber`] ?? "";
                                    const isMobileInvalid = mobileVal.replace(/\D/g, "").length !== 10;
                                    // Email must be valid format (new validation)
                                    const emailVal: string =
                                      formikRef?.current?.values?.[`basicCoApplicant${coAppNum}Email`] ?? "";
                                    const emailRegex = JOURNEY_EMAIL_REGEX;
                                    const isEmailInvalid = !emailRegex.test(emailVal);
                                    return isAadhaarVerified || isPanNotVerified || isMobileInvalid || isEmailInvalid;
                                  })()}
                                >
                                  {formikRef?.current?.values?.[
                                    `basicCoApplicant${coAppKey.replace(
                                      "CoApplicant",
                                      ""
                                    )}AadhaarVerified`
                                  ] === true ||
                                    isDocumentVerifiedCoApplicant[coAppKey] ? (
                                    <img
                                      src={VerifiedIcon}
                                      alt="arrow-narrow-right"
                                    />
                                  ) : (
                                    <></>
                                  )}

                                  {`${formikRef?.current?.values?.[
                                    `basicCoApplicant${coAppKey.replace(
                                      "CoApplicant",
                                      ""
                                    )}AadhaarVerified`
                                  ] === true ||
                                    isDocumentVerifiedCoApplicant[coAppKey]
                                    ? "Verified"
                                    : "Verify Via Digilocker"
                                    }`}

                                  {!(
                                    formikRef?.current?.values?.[
                                    `basicCoApplicant${coAppKey.replace("CoApplicant", "")}AadhaarVerified`
                                    ] === true || isDocumentVerifiedCoApplicant[coAppKey]
                                  ) && <img src={RightBlackArrow} alt="right-arrow" />
                                  }
                                </IconButton>
                              )}
                            </Grid>
                          </>
                        ) : (
                          <Grid container spacing={2}>
                            {tabLabels[currentTab] === "Address Details"
                              ? (() => {
                                const sameAsIdx = tabFields[
                                  currentTab
                                ].findIndex((f: any) =>
                                  f.name.match(
                                    /^addressCoApplicant(\d+)currentsameAsPermanent$/
                                  )
                                );
                                

                                let permanentFields =
                                  sameAsIdx !== -1
                                    ? tabFields[currentTab].slice(
                                      0,
                                      sameAsIdx
                                    )
                                    : tabFields[currentTab];

                                const currentFields =
                                  sameAsIdx !== -1
                                    ? tabFields[currentTab].slice(
                                      sameAsIdx + 1
                                    )
                                    : [];
                                const sameAsField =
                                  sameAsIdx !== -1
                                    ? tabFields[currentTab][sameAsIdx]
                                    : null;
                               
                               
                                const currentCoApplicantNumber = Number(
                                  coAppKey.replace("CoApplicant", "")
                                );
                                const sameAsOptions = [
                                  "Applicant",
                                  ...coApplicantKeys
                                    .map((key) =>
                                      Number(key.replace("CoApplicant", ""))
                                    )
                                    .filter(
                                      (coAppNumber) =>
                                        coAppNumber < currentCoApplicantNumber
                                    )
                                    .sort((a, b) => a - b)
                                    .map(
                                      (coAppNumber) =>
                                        `Co-Applicant ${coAppNumber}`
                                    ),
                                ];

                                const tempFields = JSON.parse(JSON.stringify(permanentFields));
                                tempFields?.forEach((item: any) => {
                                  if (
                                    item.name ===
                                    `addressCoApplicant${currentCoApplicantNumber}SameAs`
                                  ) {
                                    item.options.options = sameAsOptions;
                                  }
                                });
                                permanentFields = tempFields;

                                return (
                                  <>
                                  <Grid container xs={12}>
                                    <Grid item xs={12} sm={4} sx={{ paddingLeft: "16px" }}>
                                       {permanentFields[0]?.name === `addressCoApplicant${currentCoApplicantNumber}SameAs` && renderField(permanentFields[0])}
                                      </Grid>
                                      <Grid item xs={12} sm={4}></Grid><Grid item xs={12} sm={4}>
                                      </Grid>
                                    </Grid>
                                  
                                    {permanentFields.length > 0 && (
                                      <Grid item xs={12}>
                                        <SectionHeader
                                          icon={
                                            <Box
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                width: 40,
                                                height: 40,
                                                borderRadius: "8px",
                                                bgcolor: "#e5feffff",
                                              }}
                                            >
                                              <img
                                                src={locationIcon}
                                                alt="icon"
                                                style={{
                                                  width: 28,
                                                  height: 28,
                                                }}
                                              />
                                            </Box>
                                          }
                                          title={"Permanent Address"}
                                          subtitle={""}
                                        />
                                      </Grid>
                                    )}
                                    {permanentFields
                                      ?.sort(
                                        (prev: any, next: any) =>
                                          prev?.fieldOrder - next?.fieldOrder,
                                      )
                                      ?.map((field: any) => {
                                        if (field.name === `addressCoApplicant${currentCoApplicantNumber}SameAs`) {
                                          return null;
                                      }
                                        return (
                                        <Grid
                                          item
                                          xs={12}
                                          sm={6}
                                          md={4}
                                          key={field.name}
                                        >
                                          {renderField(field)}
                                        </Grid>
                                      )})}
                                    {currentFields.length > 0 && (
                                      <Grid item xs={12}>
                                        <SectionHeader
                                          icon={
                                            <Box
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                width: 40,
                                                height: 40,
                                                borderRadius: "8px",
                                                bgcolor: "#e5feffff",
                                              }}
                                            >
                                              <img
                                                src={locationIcon}
                                                alt="icon"
                                                style={{
                                                  width: 28,
                                                  height: 28,
                                                }}
                                              />
                                            </Box>
                                          }
                                          title={"Current Address"}
                                          subtitle={""}
                                        />
                                      </Grid>
                                    )}
                                    {sameAsField && (
                                      <Grid item xs={12} key={sameAsField.name}>
                                        {renderField(sameAsField)}
                                      </Grid>
                                    )}
                                    {currentFields
                                      ?.sort(
                                        (prev: any, next: any) =>
                                          prev?.fieldOrder - next?.fieldOrder,
                                      )
                                      ?.map((field: any) => (
                                        <Grid
                                          item
                                          xs={12}
                                          sm={6}
                                          md={4}
                                          key={field.name}
                                        >
                                          {renderField(field)}
                                        </Grid>
                                      ))}
                                  </>
                                );
                              })()
                              : tabLabels[currentTab] ===
                              "Employment Details" &&
                              (() => {
                                const employmentFields = tabFields[
                                  currentTab
                                ].filter((f: any) =>
                                  /^employmentCoApplicant\d+/.test(f.name),
                                );
                                const isEmployedField = employmentFields.find(
                                  (f: any) =>
                                    f.type === "radio" &&
                                    /^employmentCoApplicant\d+isEmployed$/.test(
                                      f.name
                                    )
                                );
                                const isFirstCoApplicantEmployed =
                                  isFirstCoApplicantEmploymentField(
                                    isEmployedField?.name,
                                  );
                                if (isFirstCoApplicantEmployed) {
                                  ensureFirstCoApplicantEmploymentYes(
                                    formikRef,
                                    isEmployedField?.name,
                                  );
                                }
                                const formikIsEmployedValue =
                                  isEmployedField && formikRef?.current
                                    ? formikRef.current.values[isEmployedField.name]
                                    : "Yes";
                                const isEmployedValue = isFirstCoApplicantEmployed
                                  ? "Yes"
                                  : formikIsEmployedValue ?? "Yes";
                                const employmentTypeFieldName = `employmentCoApplicant${coAppNum}employmentType`;
                                return (
                                  <>
                                    {isEmployedField && (
                                      <Grid
                                        item
                                        xs={12}
                                        key={isEmployedField.name}
                                      >
                                          {isFirstCoApplicantEmployed && (
                                            <FirstCoApplicantEmploymentNotice />
                                          )}
                                        {renderField(
                                            lockFirstCoApplicantEmploymentField(
                                              isEmployedField,
                                            ),
                                          )}
                                      </Grid>
                                    )}
                                    {isEmployedValue === "Yes" && (
                                      <>
                                        {(() => {
                                          let hasInsertedCompanyHeader = false;
                                          const allEmploymentFields =
                                            employmentFields.filter(
                                              (f: any) =>
                                                f.name !== isEmployedField?.name,
                                            );
                                          const selectedEmploymentType =
                                            getAutocompleteOptionOrValue(
                                              formikRef?.current?.values?.[
                                                employmentTypeFieldName
                                              ],
                                            );
                                          const shouldShowOnlyRequired =
                                            selectedEmploymentType ===
                                              "Others" ||
                                            selectedEmploymentType ===
                                              "Farmer";
                                          const fieldsToRender =
                                            !selectedEmploymentType
                                              ? allEmploymentFields.filter((f: any) =>
                                                  /^employmentCoApplicant\d+employmentType$/.test(
                                                    f.name,
                                                  ),
                                                )
                                              : shouldShowOnlyRequired
                                              ? allEmploymentFields.filter(
                                                  (f: any) =>
                                                    /^employmentCoApplicant\d+employmentType$/.test(
                                                      f.name,
                                                    ) ||
                                                    /^employmentCoApplicant\d+monthlyObligation$/.test(
                                                      f.name,
                                                    ) ||
                                                    /^employmentCoApplicant\d+monthlyIncome$/.test(
                                                      f.name,
                                                    ),
                                                )
                                              : allEmploymentFields;

                                          return fieldsToRender
                                            .slice()
                                            .sort(
                                              (prev: any, next: any) =>
                                                (prev?.fieldOrder ?? 0) -
                                                (next?.fieldOrder ?? 0),
                                            )
                                            .map((field: any, fieldIdx: number) => {
                                              const elements: React.ReactNode[] = [];
                                              const isEmploymentTypeField =
                                                /^employmentCoApplicant\d+employmentType$/.test(
                                                  field.name || "",
                                                );

                                              if (
                                                !hasInsertedCompanyHeader &&
                                                field.name
                                                  ?.toLowerCase()
                                                  .includes("company") &&
                                                !/^employmentCoApplicant\d+companyName$/.test(
                                                  field.name,
                                                )
                                              ) {
                                                elements.push(
                                                  <Grid
                                                    item
                                                    xs={12}
                                                    key="coapp-company-section-header"
                                                  >
                                                    <SectionHeader
                                                      icon={
                                                        <Box
                                                          sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            width: 40,
                                                            height: 40,
                                                            borderRadius: "8px",
                                                            bgcolor: "#e5feffff",
                                                          }}
                                                        >
                                                          <img
                                                            src={locationIcon}
                                                            alt="icon"
                                                            style={{
                                                              width: 28,
                                                              height: 28,
                                                            }}
                                                          />
                                                        </Box>
                                                      }
                                                      title="Company Address"
                                                      subtitle=""
                                                    />
                                                  </Grid>,
                                                );
                                                hasInsertedCompanyHeader = true;
                                              }

                                              elements.push(
                                                <Grid
                                                  item
                                                  xs={12}
                                                  sm={
                                                    fieldIdx < 1
                                                      ? 12
                                                      : field.name.match(
                                                          /^employmentCoApplicant\d+employmentYear$/,
                                                        )
                                                        ? 12
                                                        : 6
                                                  }
                                                  key={field.name}
                                                >
                                                  <Box
                                                    sx={
                                                      isEmploymentTypeField
                                                        ? {
                                                            width: {
                                                              xs: "100%",
                                                              sm: "50%",
                                                            },
                                                          }
                                                        : undefined
                                                    }
                                                  >
                                                    {renderField(
                                      withMandatoryCompanyAddress(
                                        withMandatoryEmploymentAmount(field),
                                        values,
                                      ),
                                    )}
                                                  </Box>
                                                </Grid>,
                                              );

                                              return elements;
                                            });
                                        })()}
                                      </>
                                    )}
                                  </>
                                );
                              })()}
                          </Grid>
                        )}
                      </>
                    )}
                  </Box>
                  {/* Save & Next/Save button for tab navigation */}
                  <Grid item xs={12} sx={{ mt: 2, textAlign: "left" }}>
                    <PrimaryButton
                      sx={{ px: 6 }}
                      // disabled={(() => {
                      //   // Special logic for employmentCoApplicant2isEmployed
                      //   const coAppNum = coAppKey.replace("CoApplicant", "");
                      //   const isEmployedFieldName = `employmentCoApplicant${coAppNum}isEmployed`;
                      //   const employmentTypeFieldName = `employmentCoApplicant${coAppNum}employmentType`;
                      //   const isEmployed = values?.[isEmployedFieldName];
                      //   // Filter required fields, but if isEmployed is 'No', skip employmentType as required
                      //   // Also always exclude verification flag fields (AadhaarVerified, IsPanVerified, IsMobileVerified, IsEmailVerified)
                      //   const requiredFields = tabFields[currentTab].filter(
                      //     (f: any) => {
                      //       if (
                      //         /^basicCoApplicant\d+(AadhaarVerified|IsMobileVerified|IsEmailVerified)$/.test(
                      //           f.name
                      //         )
                      //       ) {
                      //         return false;
                      //       }

                      //       // In Employment tab, if "No" is selected, only keep the isEmployed radio required.
                      //       if (
                      //         tabLabels[currentTab] === "Employment Details" &&
                      //         isEmployed === "No"
                      //       ) {
                      //         return f.name === isEmployedFieldName;
                      //       }

                      //       if (
                      //         f.name === employmentTypeFieldName &&
                      //         isEmployed === "No"
                      //       ) {
                      //         return false;
                      //       }

                      //       // For Others/Farmer employment type, only employmentType, monthlyObligation and monthlyIncome remain required.
                      //       if (
                      //         tabLabels[currentTab] === "Employment Details" &&
                      //         isEmployed === "Yes" &&
                      //         (values?.[employmentTypeFieldName] === "Others" ||
                      //           values?.[employmentTypeFieldName] === "Farmer") &&
                      //         f.name !== employmentTypeFieldName &&
                      //         f.name !==
                      //           `employmentCoApplicant${coAppNum}monthlyObligation` &&
                      //         f.name !==
                      //           `employmentCoApplicant${coAppNum}monthlyIncome`
                      //       ) {
                      //         return false;
                      //       }

                      //       return isRelationValueFieldRequired(
                      //         withMandatoryCompanyAddress(
                      //           withMandatoryEmploymentAmount(f),
                      //           values,
                      //         ),
                      //       );
                      //     }
                      //   );
                      //   const hasEmptyRequired = requiredFields.some(
                      //     (f: any) => {
                      //       const val = values?.[f.name];
                      //       return (
                      //         val === undefined || val === null || val === ""
                      //       );
                      //     }
                      //   );
                      //   // Also disable if IsPanVerified is false for this co-applicant
                      //   const isPanVerified =
                      //     values?.[`basicCoApplicant${coAppNum}IsPanVerified`] ??
                      //     false;

                      //   // For Basic Details tab, require a 10-digit mobile number
                      //   const mobileValue: string =
                      //     values?.[`basicCoApplicant${coAppNum}PhoneNumber`] ?? "";
                      //   const isMobileIncomplete =
                      //     tabLabels[currentTab] === "Basic Details" &&
                      //     mobileValue && mobileValue?.replace(/\D/g, "").length !== 10;

                      //   // For Basic Details tab, require a valid email address
                      //   const emailValue: string =
                      //     values?.[`basicCoApplicant${coAppNum}Email`] ?? "";
                      //   const emailRegex = JOURNEY_EMAIL_REGEX;
                      //   const isEmailInvalid =
                      //     tabLabels[currentTab] === "Basic Details" &&
                      //     !emailRegex.test(emailValue);

                      //   // Fields like the address "years in current
                      //   // address/city" ones are filled but still fail Yup's
                      //   // max-150 check — hasEmptyRequired only catches blank
                      //   // values, so check formik's own error state too.
                      //   // Dropdown/autocomplete fields are excluded here: their
                      //   // Yup schema only ever checks non-emptiness (no format/
                      //   // length rules), which hasEmptyRequired already covers
                      //   // from `values` directly. Their onChange handlers make
                      //   // multiple setFieldValue calls per selection (e.g. to
                      //   // also set a companion "...Name" field), and Formik
                      //   // validates each call against a stale pre-update
                      //   // snapshot — so `errors` for these fields can be left
                      //   // with a stale, invisible (untouched) entry that would
                      //   // otherwise block Save & Next until an unrelated field
                      //   // is touched and forces revalidation.
                      //   const hasTabErrors = tabFields[currentTab].some(
                      //     (f: any) =>
                      //       f?.type !== "dropdown" &&
                      //       f?.type !== "autocomplete" &&
                      //       Boolean(errors?.[f.name])
                      //   );
                
                      //   return (
                      //     hasEmptyRequired ||
                      //     hasTabErrors ||
                      //     isPanVerified === false ||
                      //     isMobileIncomplete ||
                      //     isEmailInvalid ||
                      //     zipCodeLoading ||
                      //     isSaveNextLoading
                      //   );
                      // })()}
                      onClick={async () => {
                        // Run the Co-applicant validation (AC 3/4/5) against the
                        // fields shown on the CURRENT tab only. Errors are shown
                        // on click (fields render errors only when touched), and
                        // an invalid tab blocks Save & Next — the button itself
                        // is never disabled.
                        const currentTabFieldNames: string[] = (
                          tabFields[currentTab] || []
                        )
                          .map((f: any) => String(f?.name || ""))
                          .filter(Boolean);

                        // validateForm() validates against the active Yup schema
                        // (getValidationSchema for horizontal 1 / vertical 5).
                        const allErrors =
                          (await formikRef?.current?.validateForm()) || {};

                        const currentTabErrors = currentTabFieldNames.filter(
                          (fieldName) => Boolean((allErrors as any)?.[fieldName])
                        );

                        if (currentTabErrors.length > 0) {
                          // Mark only this tab's fields as touched so their error
                          // messages become visible without touching other tabs.
                          currentTabFieldNames.forEach((fieldName) => {
                            formikRef?.current?.setFieldTouched(
                              fieldName,
                              true,
                              false
                            );
                          });
                          // setSnackbar({
                          //   open: true,
                          //   message:
                          //     "Please fill in all the required details correctly before proceeding.",
                          //   severity: "error",
                          // });
                          return;
                        }

                        handleTabSave(
                          coAppKey,
                          currentTab,
                          currentTab === tabLabels.length - 1
                        );
                        // setting empty data on button click
                        dispatch(
                          setVerificationMessage({
                            index: "",
                            applicantType: "",
                            field: "",
                            status: "",
                            message: "",
                          })
                        );
                      }}
                    >
                      {isSaveNextLoading ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <CircularProgress
                            size={16}
                            sx={{ color: "#1F2233" }}
                          />
                          <span>Loading...</span>
                        </Box>
                      ) : currentTab === tabLabels.length - 1 ? (
                        "Save"
                      ) : (
                        "Save & Next"
                      )}
                    </PrimaryButton>
                  </Grid>
                </CollapsibleSection>

                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  {/* Show Remove only for extraCoApplicant (if any field isRemoved) */}
                  {group.basic
                    .concat(group.address, group.employment)
                    .some((field: any) => field.isRemoved) ? (
                    <Grid sx={{ textAlign: "left", mt: 2 }}>
                      <Box
                        sx={{ display: "inline-flex", cursor: "pointer" }}
                        onClick={() => handleRemoveClick(group.ids)}
                      >
                        <img src={RemoveIcon} alt="arrow-narrow-right" />
                        <Typography
                          variant="subtitle2"
                          sx={{
                            ml: 1,
                            fontWeight: 600,
                            color: "#FF4C51",
                            fontSize: "13px",
                          }}
                        >
                          Remove
                        </Typography>
                      </Box>
                    </Grid>
                  ) : (
                    <Box></Box>
                  )}
                </Grid>
              </Grid>
            </>
          );
        });
      })()}

      <Grid container justifyContent="end" sx={{ mx: 2, mt: 4 }}>
        {!isXsOrSm && numberOfCoApp < 9 && (
          <Grid textAlign="right">
            <Button
              sx={{
                textTransform: "none",
                fontFamily: "Jost, sans-serif",
                fontWeight: 600,
                fontSize: "14px",
                lineHeight: "100%",
                letterSpacing: "0%",
                color: "#33c2c4",
                borderColor: "#33c2c4",
                "&:hover": {
                  backgroundColor: "transparent",
                  borderColor: "#33c2c4",
                  color: "#33c2c4",
                },
              }}
              onClick={addCoApplicantFields}
              disabled={isLoading || disableAddMoreCoApplicant}
            >
              <AddIcon sx={{ mr: 1, fontSize: "20px" }} />
              Add More
            </Button>
          </Grid>
        )}
      </Grid>

      {/* Proceed Button */}
      <Grid container justifyContent="start" sx={{ mx: 2, mt: 4, marginRight: 0 }}>
        <PrimaryButton
          sx={{ px: 6, }}
          fullWidth={isXsOrSm}
          disabled={(() => {
            // Disable if any required field is empty, but if employmentCoApplicantNisEmployed is 'No', skip employmentCoApplicantNemploymentType as required
            const values = formikRef?.current?.values || {};
            const requiredFields = fields.filter((field: any) => {
              // Check for employmentType fields
              if (
                /^basicCoApplicant\d+(AadhaarVerified|IsMobileVerified|IsEmailVerified|AadhaarNumber)$/.test(
                  field.name
                )
              ) {
                return false;
              }
              const empFieldMatch = field.name.match(
                /^employmentCoApplicant(\d+)/
              );
              if (empFieldMatch) {
                const coAppNum = empFieldMatch[1];
                const isEmployedFieldName = `employmentCoApplicant${coAppNum}isEmployed`;
                const employmentTypeFieldName = `employmentCoApplicant${coAppNum}employmentType`;
                const isEmployed = values[isEmployedFieldName];
                const selectedEmploymentType =
                  getAutocompleteOptionOrValue(values[employmentTypeFieldName]);

                // Not employed: only the isEmployed radio itself stays required.
                if (isEmployed === "No" && field.name !== isEmployedFieldName) {
                  return false;
                }

                if (
                  isEmployed === "Yes" &&
                  !selectedEmploymentType &&
                  field.name !== employmentTypeFieldName
                ) {
                  return false;
                }

                // Others/Farmer: only employmentType, monthlyObligation and monthlyIncome stay required.
                if (
                  isEmployed === "Yes" &&
                  (selectedEmploymentType === "Others" ||
                    selectedEmploymentType === "Farmer") &&
                  field.name !== employmentTypeFieldName &&
                  field.name !==
                    `employmentCoApplicant${coAppNum}monthlyObligation` &&
                  field.name !== `employmentCoApplicant${coAppNum}monthlyIncome`
                ) {
                  return false;
                }
              }
              const mandatoryField = withMandatoryCompanyAddress(
                withMandatoryEmploymentAmount(field),
                values,
              );
              if (getRelationValueFieldMatch(field.name)) {
                return isRelationValueFieldRequired(mandatoryField);
              }
              return (
                (/CoApplicant\d+/.test(field.name) ||
                  field.name === "LoanDetailsLoanAmount" ||
                  field.name === "collateral") &&
                mandatoryField.required !== false
              );
            });
           
            return requiredFields.some((f: any) => {
              const val = values[f.name];
              return val === undefined || val === null || val === "";
            });
          })()}
          onClick={() => handleProceedClick()}
        >
          Proceed for Provisional Offer
        </PrimaryButton>
      </Grid>

      <LoaderBackdrop open={Object.values(isAadharLoading).some(Boolean)} />

      {isXsOrSm && numberOfCoApp < 9 && (
        <Grid item xs={12}>
          <Button
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: "14px",
              lineHeight: "100%",
              letterSpacing: "0%",
              color: "#33c2c4",
              border: "1px solid #33c2c4",
              textAlign: "center",
              width: "100%",
            }}
            onClick={addCoApplicantFields}
            disabled={isLoading || disableAddMoreCoApplicant}
          >
            <AddIcon sx={{ mr: 1, fontSize: "20px" }} />
            Add Co-Applicant
          </Button>
        </Grid>
      )}

      {/* Confirmation Modal */}
      <ModalPopup
        isOpen={confirmOpen}
        onClose={handleCancelRemove}
        subTitle="Remove Co-Applicant"
      >
        <Box sx={{ p: 2 }}>
          <Typography>
            Are you sure you want to remove this Co-Applicant? This action cannot be
            undone.
          </Typography>
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", mt: 3, gap: 2 }}
          >
            <Button
              onClick={handleCancelRemove}
              color="primary"
              size="small"
              variant="outlined"
            >
              Cancel
            </Button>
            <PrimaryButton onClick={handleConfirmRemove} variant="contained">
              Confirm
            </PrimaryButton>
          </Box>
        </Box>
      </ModalPopup>

      {/* Alert  */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CoApplicantDetailsSection;